import { billingFr } from "./billing.fr";
import { hrFr } from "./hr.fr";
import { dashboardFr } from "./dashboard.fr";
import { paymentsFr } from "./payments.fr";
import { reportsFr } from "./reports.fr";
/**
 * Every string the console shows, keyed by ID. French is the reference
 * language: every other catalog (en.ts, de.ts…) is a `Record<MessageId, string>`
 * with exactly these keys, so a missing translation is a compile error.
 * `{name}` is replaced by the value passed to `t`. Arrows point the way the
 * text reads: "→" here, "←" in Arabic.
 */
export const fr = {
  ...reportsFr,
  ...paymentsFr,
  ...dashboardFr,
  ...billingFr,
  ...hrFr,
  // ── Brand and shell ──
  "brand.name": "Walliyo",
  "brand.initial": "W",
  "brand.console": "Console",
  "nav.aria": "Console",
  "nav.new": "NOUVEAU",
  "nav.group.configuration": "Configuration",
  "overview.eachDepartment": "Chaque département, en une ligne",
  "overview.dept.marketplace": "Opérations marché",
  "overview.dept.growth": "Ventes et croissance",
  "overview.dept.support": "Service client",
  "overview.dept.trust": "Confiance et sécurité",
  "overview.dept.finance": "Finances",
  "overview.dept.people": "Ressources humaines",
  "overview.dept.administration": "Administration",
  "overview.dept.product": "Technique",
  "overview.clickThrough": "cliquez pour y aller",
  "overview.numberToWatch": "Le chiffre à surveiller",
  "overview.whereWalliyoWorks": "Où Walliyo travaille",
  "overview.noCitiesYet": "Aucune demande ce mois-ci.",
  "overview.points": "{points} points",
  "overview.title.people.walliyoMonth": "Walliyo, {month}",
  "overview.people.tab.waiting": "Ce qui vous attend",
  "overview.people.tab.company": "L'entreprise",
  "overview.people.tab.weeks": "Les semaines à venir",
  "overview.people.askForTimeOff": "Demander des congés",
  "overview.people.joinedThisMonth": "{count} arrivées ce mois-ci",
  "overview.people.joinedThisMonth.one": "1 arrivée ce mois-ci",
  "overview.people.candidates": "{count} candidats",
  "overview.people.candidates.one": "1 candidat",
  "overview.people.medianStay": "Ans, ancienneté médiane",
  "overview.people.whereEverybodyIs": "Où se trouve chacun",
  "overview.people.oneLinePerTeam": "une ligne par équipe",
  "overview.people.noTeamsYet": "Aucune équipe pour l'instant.",
  "overview.people.noLead": "sans responsable",
  "overview.people.nobodyCovering": "Personne pour assurer",
  "overview.people.thinWeek": "{team} perd {away} personnes sur {size} la semaine du {week}.",
  "overview.people.whoIsWhere": "Qui est où",
  "overview.people.fourWeeks": "quatre semaines, et ce qui tombe dans chacune",
  "overview.people.allTimeOff": "Tous les congés",
  "overview.people.quietWeeks": "Rien ne tombe dans les quatre prochaines semaines.",
  "overview.people.mark.payrollCloses": "clôture de la paie {on}",
  "overview.people.mark.reviewsClose": "clôture des évaluations {on}",
  "overview.people.span.leave": "{days} jours de congé",
  "overview.people.span.leave.one": "1 jour de congé",
  "overview.people.span.leavePending": "{days} jours · à décider",
  "overview.people.span.leavePending.one": "1 jour · à décider",
  "overview.people.span.starts": "arrive",
  "overview.people.task.candidates": "{count} candidats vous attendent",
  "overview.people.detail.candidates": "pour {second} postes ouverts",
  "marketplace.seeAll": "Voir les {count}",
  "marketplace.findAPro": "Trouver un pro",
  "marketplace.theMarket": "Le marché, cette semaine",
  "overview.title.marketplace.theMarket": "Le marché, cette semaine",
  "overview.lede.marketplace.nobodyAnswered": "{first} personnes ont demandé quelque chose et personne n'a répondu.",
  "overview.lede.marketplace.demandNoSupply": "{first} métiers sont demandés là où aucun pro n'a la compétence.",
  "overview.lede.marketplace.quiet": "Rien n'a été publié cette semaine.",
  "overview.lede.marketplace.allAnswered": "Chaque demande publiée cette semaine a reçu un devis.",
  "overview.people.link.employees": "Employés",
  "overview.people.link.recruiting": "Recrutement",
  "overview.people.link.learning": "Formation",
  "overview.people.link.teams": "Équipes",
  "overview.administration.askedNotAnswered": "Demandé, sans réponse",
  "overview.administration.oldestFirst": "le plus ancien d'abord",
  "overview.administration.nobodyWaiting": "Personne n'attend de réponse.",
  "overview.administration.row.wantsUntil": "{subject} veut pouvoir {asked}",
  "overview.administration.row.wantsForever": "{subject} veut pouvoir {asked}, sans date de fin",
  "overview.administration.until": "jusqu'au {on}",
  "overview.administration.noEndAsked": "aucune date de fin demandée",
  "overview.administration.hoursWaiting": "{hours} heures d'attente",
  "overview.administration.hoursWaiting.one": "1 heure d'attente",
  "overview.administration.pastTheDeadline": "délai dépassé",
  "overview.administration.nobodyTookItBack": "Personne ne l'a repris",
  "overview.administration.noEndDate": "{count} accès n'ont pas de date de fin.",
  "overview.administration.noEndDate.one": "1 accès n'a pas de date de fin.",
  "overview.administration.belongToLeavers": "{leavers} appartiennent à quelqu'un qui est parti.",
  "overview.administration.belongToLeavers.one": "1 appartient à quelqu'un qui est parti.",
  "overview.administration.reviewThem": "Les revoir",
  "overview.administration.criticalCapabilities": "Capacités critiques",
  "overview.administration.ifAnyReachesOne":
    "Si l'une d'elles tombe à une personne, personne ne peut assurer son absence.",
  "overview.figure.holdCritical": "Détiennent une capacité critique",
  "overview.lede.administration.waitingAndGrants":
    "{first} personnes attendent une réponse, et {second} accès n'ont pas de date de fin.",
  "overview.lede.administration.waiting": "{first} personnes attendent une réponse.",
  "overview.lede.administration.grants": "{first} accès n'ont pas de date de fin et personne ne les a repris.",
  "overview.lede.administration.nothingWaiting": "Personne n'attend de réponse.",
  "overview.growth.step.signedUp": "Inscrits",
  "overview.growth.step.verified": "Vérifiés",
  "overview.growth.step.addedSkill": "Ont ajouté un métier",
  "overview.growth.step.quotedOnce": "Ont répondu une fois",
  "overview.growth.step.wonAJob": "Ont décroché un travail",
  "overview.growth.lost.signedUp": "jamais inscrits",
  "overview.growth.lost.verified": "ont abandonné",
  "overview.growth.lost.addedSkill": "ne l'ont jamais fait",
  "overview.growth.lost.quotedOnce": "n'ont jamais répondu",
  "overview.growth.lost.wonAJob": "n'ont jamais gagné",
  "overview.growth.stuck": "Bloqués, et à appeler",
  "overview.growth.stuckWho": "vérifiés, dans un métier demandé, n'ont jamais répondu",
  "overview.growth.nobodyStuck": "Personne n'est bloqué.",
  "overview.growth.row.workGoingUnanswered": "{subject}",
  "overview.growth.why.workGoingUnanswered":
    "vérifié il y a {days} jours · du travail dans son métier reste sans réponse",
  "overview.growth.row.neverQuoted": "{subject}",
  "overview.growth.why.neverQuoted": "vérifié il y a {days} jours · n'a jamais répondu à une demande",
  "overview.growth.promoCost":
    "Elle a donné {given} en remises contre {earned} de commissions venant des pros qui l'ont utilisée.",
  "overview.growth.promoUsed": "{used} pros l'ont utilisée",
  "overview.growth.beforeYouCampaign": "Avant de lancer une campagne",
  "overview.growth.notificationsAreFree":
    "Les notifications dans l'application atteignent ces pros gratuitement. Le SMS coûte par message, et ici les données se paient à l'heure.",
  "overview.action.call": "Appeler",
  "overview.lede.growth.neverQuoted":
    "{first} pros se sont inscrits et n'ont jamais répondu à quoi que ce soit. C'est tout le travail.",
  "overview.lede.growth.nobodySignedUp": "Personne ne s'est inscrit ce mois-ci.",
  "overview.lede.growth.allWorking": "Chaque pro inscrit a répondu à quelque chose.",
  "overview.support.dueInMinutes.one": "échéance dans 1 minute",
  "overview.support.dueInHours.one": "échéance dans 1 heure",
  "overview.support.overdue.one": "échéance dépassée d'1 minute",
  "overview.support.nobodyOwnsWhy.one":
    "Un dossier sans titulaire est un dossier auquel personne ne répond. Le plus ancien attend depuis 1 jour.",
  "overview.people.days.one": "1 jour",
  "overview.people.payrollOpen.one": "{month} est ouvert. Il se clôture demain.",
  "overview.people.detail.leave.one": "Demandé il y a 1 jour · vous êtes son responsable",
  "overview.finance.waiting.one": "1 jour d'attente",
  "overview.trust.row.appealWaiting.one": "Recours en attente depuis 1 jour",
  "overview.trust.legalHoldsWhy.one":
    "1 compte est conservé. Rien à son sujet ne peut être supprimé, même par une demande de données.",
  "overview.product.unconfirmed.one": "1 rappel non confirmé, il y a {minutes} minutes.",
  "overview.product.whatIsBroken": "Ce qui est cassé",
  "overview.product.worstFirst": "le plus grave d'abord",
  "overview.product.nothingBroken": "Rien n'est cassé.",
  "overview.product.unconfirmed": "{count} rappels non confirmés, le plus ancien il y a {minutes} minutes.",
  "overview.product.unconfirmedWhy": "Chacun est un pro qui a payé et qui n'est pas marqué payé.",
  "overview.product.replayThem": "Les rejouer",
  "overview.product.rolledOut": "Déployé chez les clients",
  "overview.product.tally.everybody": "Tout le monde",
  "overview.product.tally.someAccounts": "Certains comptes",
  "overview.product.tally.off": "Désactivé",
  "overview.figure.rolloutsInFlight": "Déploiements en cours",
  "overview.product.row.incidentOpen": "{subject}",
  "overview.product.why.incidentOpen": "ouvert depuis {amount} · les clients voient la bannière",
  "overview.product.row.ruleScheduled": "La règle prend effet {at}",
  "overview.product.why.ruleScheduled": "{subject} {amount} · personne n'a validé",
  "overview.product.row.experimentRunning": "{subject} chez {days} % des clients",
  "overview.product.why.experimentRunning": "en cours depuis le {on} · prêt à être tranché",
  "overview.figure.incidentsOpen": "Incidents ouverts",
  "overview.lede.product.incidentAndCallbacks":
    "{first} incident est ouvert, et {second} rappels de paiement ont échoué.",
  "overview.lede.product.incident": "{first} incident est ouvert.",
  "overview.lede.product.callbacks": "{first} rappels de paiement ont échoué et personne ne les a rejoués.",
  "overview.lede.product.rulesScheduled": "{first} règles prennent effet sans personne.",
  "overview.lede.product.nothingBroken": "Rien n'est cassé.",
  "overview.finance.aboveLimit": "Au-dessus de la limite de quelqu'un",
  "overview.finance.theyAskedYouDecide": "ils ont demandé, vous décidez",
  "overview.finance.nothingWaitingHere": "Aucune approbation en attente.",
  "overview.finance.theirLimit": "limite {ceiling}",
  "overview.finance.noLimit": "ne détient aucune limite",
  "overview.finance.waiting": "{days} jours d'attente",
  "overview.finance.today": "aujourd'hui",
  "overview.finance.subscriptions": "Abonnements",
  "overview.finance.tally.endingSoon": "Renouvellement sous 7 jours",
  "overview.finance.tally.suspended": "Paiement échoué",
  "overview.finance.tally.ended": "Terminés, non renouvelés",
  "overview.finance.promosRunning": "Promotions en cours",
  "overview.finance.promoEnds": "{name} se termine le {on}.",
  "overview.finance.promoNoEnd": "{name} n'a pas de date de fin.",
  "overview.finance.promoCost": "Elle a donné {given} en remises contre {earned} de commissions.",
  "overview.figure.feesCollected": "Commissions encaissées",
  "overview.figure.jobsBilled": "Travaux facturés",
  "overview.figure.renewalsFailed": "Renouvellements échoués",
  "overview.lede.finance.waitingOnApproval": "{first} corrections attendent quelqu'un qui puisse les approuver.",
  "overview.lede.finance.renewalsFailed": "{first} renouvellements ont échoué et personne ne les a repris.",
  "overview.lede.finance.renewingSoon": "{first} abonnements se renouvellent cette semaine.",
  "overview.lede.finance.nothingWaiting": "Aucune approbation en attente.",
  "overview.trust.needsDecision": "À décider aujourd'hui",
  "overview.trust.mostSeriousFirst": "le plus grave d'abord",
  "overview.trust.nothingUrgentHere": "Rien à décider aujourd'hui.",
  "overview.trust.verificationQueue": "File de vérification",
  "overview.trust.whyVerify": "Un pro en attente de vérification ne peut être ni trouvé ni engagé.",
  "overview.trust.tally.underADay": "En attente depuis moins de 24 h",
  "overview.trust.tally.overADay": "Plus de 24 h",
  "overview.trust.legalHolds": "Conservations légales",
  "overview.trust.legalHoldsWhy":
    "{count} comptes sont conservés. Rien à leur sujet ne peut être supprimé, même par une demande de données.",
  "overview.trust.row.criticalUnreferred": "{subject}",
  "overview.trust.why.criticalUnreferred": "critique · non transmis à la police",
  "overview.trust.row.restrictionExpiring": "La sanction se lève d'elle-même le {on}",
  "overview.trust.why.restrictionExpiring": "{subject} · levez-la, ou elle se lèvera seule",
  "overview.trust.row.appealWaiting": "Recours en attente depuis {days} jours",
  "overview.trust.why.appealWaiting": "un compte sanctionné a demandé un réexamen",
  "overview.figure.underLegalHold": "Sous conservation légale",
  "overview.lede.trust.criticalUnreferred": "{first} signalements sont critiques et aucun n'a été transmis.",
  "overview.lede.trust.waitingOverADay": "{first} pros attendent leur vérification depuis plus d'un jour.",
  "overview.lede.trust.waitingToVerify": "{first} pros attendent leur vérification.",
  "overview.lede.trust.nothingUrgent": "Rien à décider aujourd'hui.",
  "overview.support.runningOut": "Le temps presse",
  "overview.support.soonestFirst": "échéance la plus proche d'abord",
  "overview.support.nothingDue": "Aucun dossier proche de son échéance.",
  "overview.support.whereFrom": "D'où ils viennent",
  "overview.support.noChannels": "Aucun dossier ouvert.",
  "overview.support.channel.chat": "Dans l'application",
  "overview.support.channel.phone": "Téléphone",
  "overview.support.channel.email": "E-mail",
  "overview.support.nobodyOwns": "{count} sans titulaire",
  "overview.support.nobodyOwnsWhy":
    "Un dossier sans titulaire est un dossier auquel personne ne répond. Le plus ancien attend depuis {days} jours.",
  "overview.support.takeOldest": "Prendre le plus ancien",
  "overview.support.row.mine": "le vôtre",
  "overview.support.row.theirs": "celui de quelqu'un d'autre",
  "overview.support.row.unclaimed": "sans titulaire",
  "overview.support.dueInMinutes": "échéance dans {minutes} minutes",
  "overview.support.dueInHours": "échéance dans {hours} heures",
  "overview.support.overdue": "échéance dépassée de {minutes} minutes",
  "overview.figure.toFirstReply": "Avant la première réponse",
  "overview.action.reply": "Répondre",
  "overview.action.claim": "Prendre",
  "overview.lede.support.breaching": "{first} dossiers vont dépasser leur échéance.",
  "overview.lede.support.breachingSomeYours": "{first} dossiers vont dépasser leur échéance. {second} sont les vôtres.",
  "overview.lede.support.unclaimed": "{first} dossiers attendent que quelqu'un les prenne.",
  "overview.lede.support.nothingBreaching": "Aucun dossier ne va dépasser son échéance.",
  "overview.against": "contre {month}",
  "overview.title.executive.walliyoMonth": "Walliyo, {month}",
  "overview.title.people.thingsNeedYou": "{count} choses vous attendent",
  "overview.title.people.nothingNeedsYou": "Rien ne vous attend",
  "overview.title.support.bonjour": "Bonjour, {name}",
  "overview.title.trust.safetyToday": "Sécurité, aujourd'hui",
  "overview.title.finance.theMoney": "L'argent, cette semaine",
  "overview.title.product.themOrUs": "Est-ce eux, ou est-ce nous",
  "overview.title.growth.gettingProsWorking": "Faire travailler les pros",
  "overview.title.administration.whoMayDoWhat": "Qui peut faire quoi",
  "overview.title.marketplace.plain": "Vue d'ensemble",
  "overview.title.legal.plain": "Vue d'ensemble",
  "overview.lede.executive.grewFaster": "Le marché a grandi. Ce qu'il n'a pas pu servir a grandi plus vite.",
  "overview.lede.executive.wentUnanswered": "{first} demandes sont restées sans réponse ce mois-ci.",
  "overview.lede.executive.quiet": "Aucune demande publiée ce mois-ci.",
  "overview.lede.executive.holding": "{second} demandes publiées, et chacune a trouvé une réponse.",
  "overview.people.allClear": "Rien ne vous attend. Tout est traité.",
  "overview.people.theCompany": "L'entreprise",
  "overview.people.employees": "employés",
  "overview.people.teams": "équipes",
  "overview.people.yours": "Pour vous",
  "overview.people.leaveLeft": "Congés restants",
  "overview.people.days": "{days} jours",
  "overview.people.noAllowance": "non défini",
  "overview.people.coursesDue": "Formations à faire",
  "overview.people.payroll": "Paie",
  "overview.people.payrollOpen": "{month} est ouvert. Il se clôture dans {days} jours.",
  "overview.people.payrollDue": "{month} est ouvert et sa date de clôture est passée.",
  "overview.people.payrollClosed": "{month} est clôturé.",
  "overview.people.linesChecked": "{checked} lignes vérifiées sur {total}",
  "overview.people.task.leave": "{subject} demande {count} jours de congé",
  "overview.people.detail.leave": "Demandé il y a {days} jours · vous êtes son responsable",
  "overview.people.task.grantsNoEndDate": "{count} accès sans date de fin",
  "overview.people.detail.grantsNoEndDate": "{second} appartiennent à des personnes que vous encadrez",
  "overview.people.task.reviews": "{count} évaluations à écrire",
  "overview.people.detail.reviews": "se clôture le {on}",
  "overview.people.task.starting": "{subject} arrive {on}",
  "overview.people.detail.starting": "rôle déjà attribué",
  "overview.people.task.startingNoRole": "{subject} arrive {on}",
  "overview.people.detail.startingNoRole": "aucun rôle attribué",
  "overview.action.decide": "Décider",
  "overview.action.review": "Revoir",
  "overview.action.write": "Écrire",
  "overview.action.setUp": "Configurer",
  "overview.action.open": "Ouvrir",
  "overview.hero.jobsPosted": "Demandes publiées",
  "overview.hero.gotAQuote": "Ont reçu un devis",
  "overview.hero.fees": "Commissions, GNF",
  "overview.hero.activePros": "Pros actifs",
  "overview.hero.employees": "Employés",
  "overview.headline.unanswered": "{count} demandes sans réponse · {second} métiers non couverts",
  "overview.headline.neverQuoted": "{count} pros inscrits n'ont jamais répondu",
  "overview.headline.breaching": "{count} dossiers bientôt hors délai · {second} sans titulaire",
  "overview.headline.criticalUnreferred":
    "{count} signalements critiques non transmis · {second} vérifications en attente",
  "overview.headline.approvalsWaiting": "{count} corrections à approuver · {second} en retard",
  "overview.headline.approvalsWaiting.zero": "{count} corrections à approuver · rien en retard",
  "overview.headline.grantsWithoutExpiry": "{count} accès sans date de fin · {second} demandes d'accès",
  "overview.headline.incidentsOpen": "{count} incident en cours · {second} rappels en échec",
  "overview.headline.startingAndPayroll": "{count} arrivées · paie ouverte",
  "overview.watch.demandOutrunningSupply":
    "Les demandes montent de {first} % et la part qui reçoit un devis baisse de {second} points. La demande arrive plus vite que l'offre ne répond.",
  "overview.watch.nobodyAnswered": "{first} demandes ont été publiées sans qu'un pro réponde.",
  "roles.new.department": "Département",
  "roles.new.pickDepartment": "Choisir un département…",
  "roles.new.departmentNeeded":
    "Choisissez le département du rôle : un QA testeur est Produit et technique, un comptable est Finances.",
  "screen.experiments": "Expériences",
  "screen.leads": "Prospects",
  "screen.campaigns": "Campagnes",
  "screen.referrals": "Parrainages",
  "experiments.title": "Expériences",
  "experiments.sub": "Activer une fonctionnalité pour une part des clients, et comparer.",
  "experiments.loadFailed": "Les expériences n'ont pas pu être chargées.",
  "experiments.none": "Aucune expérience.",
  "experiments.start": "Lancer une expérience",
  "experiments.created": "{reference} créée",
  "experiments.moved": "{reference} à {percent}%",
  "experiments.ship": "Généraliser",
  "experiments.stop": "Arrêter",
  "experiments.sticky": "Une personne reste dans son groupe : le groupe est calculé, jamais tiré au sort.",
  "experiments.col.name": "Expérience",
  "experiments.col.feature": "Fonctionnalité",
  "experiments.col.share": "Part",
  "experiments.col.status": "État",
  "experiments.col.ramp": "Étendre",
  "experiments.status.draft": "Brouillon",
  "experiments.status.running": "En cours",
  "experiments.status.stopped": "Arrêtée",
  "experiments.status.shipped": "Généralisée",
  "experiments.startTitle": "Lancer une expérience",
  "experiments.startBody": "Elle démarre à 0% : la créer et l'ouvrir sont deux décisions.",
  "experiments.startSubmit": "Créer",
  "experiments.featureNeeded": "Choisissez une fonctionnalité client.",
  "experiments.pickFeature": "Choisir…",
  "experiments.field.name": "Ce qu'on essaie",
  "experiments.field.feature": "Fonctionnalité client",
  "experiments.field.featureHint": "Une expérience porte sur les clients, jamais sur le personnel.",
  "experiments.field.metric": "Ce qu'on mesure",
  "experiments.field.metricHint": "Lu par une personne, pas par le code.",
  "leads.title": "Prospects",
  "leads.sub": "Des pros qu'on veut et qu'on n'a pas encore.",
  "leads.loadFailed": "Les prospects n'ont pas pu être chargés.",
  "leads.none": "Aucun prospect.",
  "leads.add": "Ajouter un prospect",
  "leads.added": "{name} ajouté",
  "leads.moved": "{name} mis à jour",
  "leads.markContacted": "Contacté",
  "leads.markLost": "Perdu",
  "leads.nameNeeded": "Donnez le nom du pro.",
  "leads.status.new": "Nouveau",
  "leads.status.contacted": "Contacté",
  "leads.status.signed_up": "Inscrit",
  "leads.status.lost": "Perdu",
  "leads.source.coverage_gap": "Manque de couverture",
  "leads.source.referral": "Recommandation",
  "leads.source.inbound": "Entrant",
  "leads.source.other": "Autre",
  "leads.addTitle": "Ajouter un prospect",
  "leads.addBody": "Quelqu'un qui n'est pas encore sur la plateforme.",
  "leads.addSubmit": "Ajouter",
  "leads.field.name": "Nom",
  "leads.field.phone": "Téléphone",
  "leads.field.trade": "Métier",
  "leads.field.city": "Ville",
  "leads.field.source": "Origine",
  "campaigns.title": "Campagnes",
  "campaigns.sub": "Des messages aux pros et aux clients.",
  "campaigns.loadFailed": "Les campagnes n'ont pas pu être chargées.",
  "campaigns.none": "Aucune campagne.",
  "campaigns.draft": "Préparer une campagne",
  "campaigns.drafted": "{reference} prête : {count} destinataires",
  "campaigns.send": "Envoyer",
  "campaigns.sent": "Envoyée à {count} personnes",
  "campaigns.wentTo": "Envoyée à {count}",
  "campaigns.budgetNeeded": "Une campagne SMS a besoin d'un budget.",
  "campaigns.onlyInactive": "Seulement ceux qui n'ont rien fait",
  "campaigns.inAppFree": "Gratuit : la notification arrive dans l'application.",
  "campaigns.smsCosts": "Payant : chaque SMS coûte, et ici les données se paient à l'heure.",
  "campaigns.smsLength": "{count} caractères (au-delà de 160, deux SMS par personne)",
  "campaigns.channel.in_app": "Dans l'application",
  "campaigns.channel.sms": "SMS",
  "campaigns.audience.pros": "Pros",
  "campaigns.audience.customers": "Clients",
  "campaigns.draftTitle": "Préparer une campagne",
  "campaigns.draftBody": "Le nombre de destinataires est recompté à l'envoi.",
  "campaigns.draftSubmit": "Préparer",
  "campaigns.field.name": "Nom",
  "campaigns.field.channel": "Canal",
  "campaigns.field.audience": "Destinataires",
  "campaigns.field.body": "Message",
  "campaigns.field.bodyHint": "En français, vu par tout le monde.",
  "campaigns.field.budget": "Budget (GNF)",
  "campaigns.field.budgetHint": "Ce qu'on s'autorise à dépenser.",
  "referrals.title": "Parrainages",
  "referrals.sub": "Des pros amenés par d'autres pros.",
  "referrals.loadFailed": "Les parrainages n'ont pas pu être chargés.",
  "referrals.none": "Aucun parrainage.",
  "referrals.unused": "Code non utilisé",
  "referrals.paidOut": "{amount} {currency} versés",
  "referrals.paidWhen": "Versé quand le pro parrainé décroche son premier travail.",
  "referrals.status.open": "Ouvert",
  "referrals.status.signed_up": "Inscrit",
  "referrals.status.rewarded": "Versé",
  "referrals.status.expired": "Expiré",
  "screen.incidents": "Incidents",
  "incidents.title": "Incidents",
  "incidents.sub": "Ce qui est cassé, et ce qu'on en dit aux gens.",
  "incidents.loadFailed": "Les incidents n'ont pas pu être chargés.",
  "incidents.openOne": "Ouvrir un incident",
  "incidents.happeningNow": "En cours",
  "incidents.allWell": "Tout fonctionne.",
  "incidents.over": "Terminés",
  "incidents.open": "Ouvrir",
  "incidents.notAnnounced": "Non annoncé",
  "incidents.started": "Commencé le {when}",
  "incidents.opened": "{reference} ouvert",
  "incidents.updated": "{reference} mis à jour",
  "incidents.status.open": "En cours",
  "incidents.status.monitoring": "Sous surveillance",
  "incidents.status.resolved": "Terminé",
  "incidents.severity.degraded": "Dégradé",
  "incidents.severity.down": "Hors service",
  "incidents.openTitle": "Ouvrir un incident",
  "incidents.openBody":
    "Un bandeau s'affiche devant chaque client tant que l'incident est ouvert. Laissez-le vide pour suivre l'incident sans rien annoncer.",
  "incidents.openSubmit": "Ouvrir",
  "incidents.titleNeeded": "Dites ce qui est cassé, en quelques mots.",
  "incidents.causeNeeded": "Dites pourquoi c'est arrivé avant de clore.",
  "incidents.updateSubmit": "Enregistrer",
  "incidents.field.title": "Ce qui est cassé",
  "incidents.field.severity": "Gravité",
  "incidents.field.banner": "Message aux clients",
  "incidents.field.bannerHint": "En français, vu par tout le monde. Vide : personne n'est prévenu.",
  "incidents.field.status": "État",
  "incidents.field.note": "Note",
  "incidents.field.noteHint": "Ajoutée au journal, que rien ne peut réécrire.",
  "incidents.field.cause": "Cause",
  "screen.overview": "Vue d'ensemble",
  "overview.sub": "Ce qui vous attend aujourd'hui.",
  "overview.loadFailed": "La vue d'ensemble n'a pas pu être chargée.",
  "overview.needsYou": "Ce qui vous attend",
  "overview.nothingToShow": "Rien à afficher ici : vous ne détenez rien de ce que ce département consulte.",
  "overview.waitingDays": "{days} j d'attente",
  "overview.figure.leaveToDecide": "Congés à décider",
  "overview.figure.reviewsOwed": "Évaluations à écrire",
  "overview.figure.openRoles": "Postes ouverts",
  "overview.figure.startingSoon": "Arrivées cette semaine",
  "overview.figure.payrollOpen": "Paies ouvertes",
  "overview.figure.leaversStillHolding": "Partis détenant encore un accès",
  "overview.figure.accessRequests": "Demandes d'accès",
  "overview.figure.grantsWithoutExpiry": "Accès sans date de fin",
  "overview.figure.rolesHeldByNobody": "Rôles que personne ne détient",
  "overview.figure.staffWithSignIn": "Employés actifs",
  "overview.figure.casesBreaching": "Dossiers bientôt hors délai",
  "overview.figure.casesMine": "Vos dossiers",
  "overview.figure.casesUnclaimed": "Dossiers sans titulaire",
  "overview.figure.casesOpen": "Dossiers ouverts",
  "overview.figure.criticalUnreferred": "Signalements critiques non transmis",
  "overview.figure.verificationsWaiting": "Vérifications en attente",
  "overview.figure.verificationsOverADay": "En attente depuis plus de 24 h",
  "overview.figure.reportsOpen": "Signalements ouverts",
  "overview.figure.restrictionsToReview": "Sanctions à revoir",
  "overview.figure.legalHolds": "Conservations légales",
  "overview.figure.approvalsWaiting": "Corrections à approuver",
  "overview.figure.feesThisMonth": "Paiements ce mois-ci",
  "overview.figure.creditsOutstanding": "Avoirs en cours",
  "overview.figure.subscriptionsEndingSoon": "Abonnements finissant sous 7 j",
  "overview.figure.providerEventsFailed": "Rappels fournisseur en échec",
  "overview.figure.rulesScheduled": "Règles programmées",
  "overview.figure.featureExceptions": "Exceptions de fonctionnalité",
  "overview.figure.prosSignedUp": "Pros inscrits (30 j)",
  "overview.figure.prosApproved": "Pros approuvés (30 j)",
  "overview.figure.prosNeverQuoted": "Pros n'ayant jamais répondu",
  "overview.figure.prosWithWork": "Pros ayant décroché un travail",
  "overview.figure.promosRunning": "Promotions en cours",
  "overview.figure.jobsThisMonth": "Demandes ce mois-ci",
  "overview.figure.customers": "Comptes clients",
  "overview.item.caseDueOwned": "Votre dossier arrive à échéance",
  "overview.item.caseDueUnclaimed": "Dossier sans titulaire à échéance",
  "screen.marketplaceOverview": "Vue d'ensemble",
  "screen.jobs": "Demandes",
  "screen.providers": "Prestataires",
  "screen.categories": "Métiers",
  "screen.marketplaceReviews": "Avis",
  "marketplace.title": "Le marché",
  "marketplace.sub": "Ce qui a été demandé, qui peut le faire, et où personne ne peut.",
  "marketplace.loadFailed": "Le marché n'a pas pu être chargé.",
  "marketplace.posted": "Demandes publiées",
  "marketplace.gotQuote": "Ont reçu un devis",
  "marketplace.accepted": "Acceptées",
  "marketplace.noBids": "Personne n'a répondu",
  "marketplace.toFirstQuote": "Jusqu'au 1er devis",
  "marketplace.hoursShort": "{hours} h",
  "marketplace.nobodyBid": "Personne n'a répondu à celles-ci",
  "marketplace.nobodyBidHint": "Les plus anciennes d'abord.",
  "marketplace.allAnswered": "Toutes les demandes ont reçu une réponse.",
  "marketplace.waitingDays": "{days} j d'attente",
  "marketplace.noProHere": "aucun pro de ce métier ici",
  "marketplace.prosNearby": "{count} pros à proximité",
  "marketplace.demandNoSupply": "Demande sans offre",
  "marketplace.demandNoSupplyHint": "Demandé ces 30 derniers jours, aucun pro n'a le métier sur place.",
  "marketplace.everywhereCovered": "Chaque ville a des pros pour ce qu'on y demande.",
  "marketplace.activePros": "{count} pros peuvent travailler aujourd'hui",
  "marketplace.jobs": "Demandes",
  "marketplace.jobsSub": "Chaque demande, et jusqu'où elle est allée.",
  "marketplace.noJobs": "Aucune demande ne correspond.",
  "marketplace.filter.all": "Toutes",
  "marketplace.filter.no_quotes": "Sans devis",
  "marketplace.filter.requested": "Publiées",
  "marketplace.filter.quoted": "Avec devis",
  "marketplace.filter.accepted": "Acceptées",
  "marketplace.filter.cancelled": "Annulées",
  "marketplace.status.requested": "Publiée",
  "marketplace.status.quoted": "Avec devis",
  "marketplace.status.accepted": "Acceptée",
  "marketplace.status.cancelled": "Annulée",
  "marketplace.status.declined": "Refusée",
  "marketplace.status.expired": "Expirée",
  "marketplace.col.job": "Demande",
  "marketplace.col.city": "Ville",
  "marketplace.col.posted": "Publiée",
  "marketplace.col.quotes": "Devis",
  "marketplace.col.estimate": "Estimation",
  "marketplace.col.status": "État",
  "marketplace.providers": "Prestataires",
  "marketplace.providersSub": "Qui peut travailler, et qui ne le fait pas encore.",
  "marketplace.noPros": "Aucun prestataire ne correspond.",
  "marketplace.neverQuoted": "N'ont jamais répondu",
  "marketplace.allPros": "Tous",
  "marketplace.unverified": "Non vérifié",
  "marketplace.col.pro": "Pro",
  "marketplace.col.skills": "Métiers",
  "marketplace.col.quotesGiven": "Devis donnés",
  "marketplace.col.rating": "Note",
  "marketplace.categories": "Métiers",
  "marketplace.categoriesSub": "Ce qu'on demande, et si quelqu'un sait le faire.",
  "marketplace.noCategories": "Aucun métier à afficher.",
  "marketplace.noDemand": "Aucune demande",
  "marketplace.coverageHint": "La couverture est la part des demandes ayant reçu au moins un devis.",
  "marketplace.col.category": "Métier",
  "marketplace.col.jobs": "Demandes",
  "marketplace.col.pros": "Pros",
  "marketplace.col.coverage": "Couverture",
  "marketplace.col.uncovered": "Villes sans pro",
  "marketplace.reviews": "Avis",
  "marketplace.reviewsSub": "Ce que les clients disent des pros.",
  "marketplace.noReviews": "Aucun avis ne correspond.",
  "marketplace.poorOnly": "3 étoiles ou moins",
  "marketplace.allReviews": "Tous",
  "marketplace.averageIs": "Moyenne {average} sur {total} avis",
  "nav.group.everyone": "Tout le monde",
  "workspace.yours": "Vos espaces",
  "department.executive": "Direction",
  "department.executive.blurb": "L'entreprise, en un coup d'œil",
  "department.marketplace": "Opérations marché",
  "department.marketplace.blurb": "Demandes, prestataires, métiers",
  "department.support": "Service client",
  "department.support.blurb": "Dossiers, clients et pros",
  "department.trust": "Confiance et sécurité",
  "department.trust.blurb": "Vérifications, signalements, sanctions",
  "department.finance": "Finances",
  "department.finance.blurb": "Commissions, corrections, abonnements",
  "department.growth": "Ventes et croissance",
  "department.growth.blurb": "Recrutement de pros, promotions",
  "department.product": "Produit et technique",
  "department.product.blurb": "Incidents, règles, déploiements",
  "department.people": "Ressources humaines",
  "department.people.blurb": "Embauche, emploi, paie",
  "department.administration": "Administration et sécurité",
  "department.administration.blurb": "Rôles, permissions, journal",
  "department.legal": "Juridique et conformité",
  "department.legal.blurb": "Conditions, données, demandes légales",
  "nav.signedInAs": "Connecté en tant que",
  "nav.screenCount": "{roles} · {visible} écrans sur {total}",
  "profile.title": "Votre profil",
  "profile.open": "Ouvrir votre profil",
  "profile.identity": "Vous",
  "profile.email": "E-mail",
  "profile.roles": "Rôles",
  "profile.permits": "Ce que vos rôles vous permettent",
  "profile.screensOpen": "{visible} écrans sur {total} vous sont ouverts",
  "profile.actionCount": "{count} actions que vous pouvez faire",
  "profile.noPermissions": "Aucune permission avec plafond : rien de ce que vous faites ne demande d’approbation.",
  "profile.preferences": "Préférences",
  "profile.emailLabel": "E-mail :",
  "profile.uuidLabel": "UUID:",
  "profile.rolesLabel": "Rôles",
  "profile.figure.screens": "écrans qui vous sont ouverts",
  "profile.figure.actions": "actions que vous pouvez faire",
  "profile.figure.ceilings": "avec un plafond",
  "profile.ceilingHeading": "Le maximum que vous pouvez faire seul",
  "profile.ceilingHint":
    "Au-dessus d'un plafond l'action s'ouvre quand même — votre tentative devient une demande d'approbation.",
  "profile.signOutHint": "Vous devrez vous reconnecter.",
  "header.signOut": "Se déconnecter",
  "header.signingOut": "Déconnexion…",
  "header.language": "Langue de la console",
  "header.theme": "Thème",
  "header.theme.system": "Système",
  "header.theme.light": "Clair",
  "header.theme.dark": "Sombre",
  "header.languageNotSaved": "Pour cette session seulement : ce choix n'est pas encore enregistré dans votre compte.",
  "shell.loading": "Chargement de la console…",
  "shell.loadFailed": "Impossible de charger votre profil",
  "shell.retry": "Réessayer",
  "shell.refreshFailed": "Vos droits n'ont pas pu être actualisés. La console affiche ceux chargés à {time}.",
  "shell.noScreens": "Aucun écran n'est ouvert pour vos rôles ({roles}). Demandez l'accès à un responsable.",
  "shell.noRoles": "Aucun écran ne vous est ouvert pour l'instant. Demandez un rôle à un responsable.",
  "connection.retrying": "Connexion instable · nouvelle tentative {attempt} sur {max}…",

  // ── Screens ──
  "screen.dashboard": "Tableau de bord",
  "screen.myRequests": "Mes demandes",
  "screen.myTimeOff": "Mes congés",
  "myTimeOff.sub": "Votre solde, ce que vous avez demandé, et qui d'autre est absent.",
  "myRequests.sub": "Ce que vous avez demandé à pouvoir faire, et où en est chaque réponse.",
  "screen.as.directory": "Annuaire",
  "screen.as.market": "Marché",
  "screen.as.employees": "Employés",
  "screen.as.recruiting": "Recrutement",
  "screen.as.learning": "Formation",
  "screen.as.payroll": "Paie",
  "screen.as.money": "Argent",
  "screen.as.workforce": "Effectifs",
  "screen.as.accounts": "Comptes",
  "screen.cases": "Dossiers",
  "screen.customers": "Clients & pros",
  "screen.verifications": "Vérifications",
  "screen.reports": "Signalements",
  "screen.billing": "Tarifs et abonnements",
  "screen.payments": "Paiements",
  "screen.features": "Activations des fonctionnalités",
  "screen.userSettings": "Réglages utilisateur",
  "settings.title": "Réglages utilisateur",
  "settings.tab.app": "Utilisateur de l'app",
  "settings.tab.employee": "Employé",
  "settings.col.setting": "Réglages",
  "settings.default": "Valeur par défaut",
  "settings.setHere": "Défini pour cette personne",
  "settings.clear": "Effacer",
  "settings.none": "Aucun réglage ne correspond.",
  "screen.actions": "Actions",
  "actions.title": "Actions",
  "actions.col.action": "Action",
  "actions.effect.read": "Lecture",
  "actions.effect.write": "Écriture",
  "actions.alsoGates": "+{count} autres",
  "actions.col.area": "Zone",
  "actions.col.effect": "Effet",
  "actions.col.audited": "Audité",
  "actions.col.needs": "Exige",
  "actions.needs.nothing": "Rien",
  "actions.yes": "Oui",
  "actions.no": "Non",
  "actions.surface.system": "Système",
  "actions.search": "Rechercher une action",
  "actions.none": "Aucune action ne correspond.",
  "screen.permissions": "Permissions",
  "permissions.title": "Permissions",
  "permissions.tab.role": "Rôle",
  "permissions.tab.employee": "Employé",
  "permissions.col.limit": "Plafond",
  "permissions.col.expires": "Expire le",
  "permissions.source.staffRole": "Par rôle",
  "permissions.source.staffMember": "Dérogation",
  "permissions.source.allStaff": "Tout le personnel",
  "permissions.source.default": "Aucune",
  "permissions.noLimit": "Sans plafond",
  "permissions.featureOff": "Fonctionnalité désactivée",
  "permissions.approvable": "Peut demander",
  "permissions.pickRole": "Sélectionner un rôle",
  "audit.kind.featureEnablements": "Activations des fonctionnalités",
  "audit.kind.permissions": "Permissions",
  "audit.kind.teams": "Rôles et équipes",
  "audit.kind.actions": "Actions",
  "audit.kind.userSettings": "Réglages utilisateur",
  "screen.roles": "Rôles",
  "roles.title": "Rôles",
  "roles.grants.none": "N'accorde encore rien",
  "roles.grants.permissions": "Modifier ses permissions",
  "roles.grants.features": "Modifier ses fonctionnalités",
  "roles.explain":
    "Ce qu'une personne sait et est habilitée à faire. Un rôle est le même dans toutes les équipes : diriger n'en est donc pas un, cela se définit dans Équipes.",
  "roles.col.name": "Rôle",
  "roles.col.description": "Ce qu'il couvre",
  "roles.col.members": "Personnes",
  "roles.none": "Aucun rôle pour l'instant.",
  "roles.loading": "Chargement des rôles…",
  "roles.loadFailed": "Impossible de lire les rôles.",
  "roles.retry": "Réessayer",
  "roles.edit": "Renommer",
  "roles.save": "Enregistrer",
  "roles.cancel": "Annuler",
  "roles.created": "Rôle créé.",
  "roles.new": "Nouveau rôle",
  "roles.new.title": "Nouveau rôle",
  "roles.back": "← Tous les rôles",
  "roles.opens": "Ce qu'il ouvre",
  "roles.opens.explain":
    "Les écrans que ce rôle peut atteindre, d'après les cases cochées. Pas une liste séparée : un écran apparaît parce que le rôle détient ce qu'il faut, et l'API vérifie la même chose.",
  "roles.new.name": "Nom",
  "roles.new.description": "Ce qu'il couvre",
  "roles.new.create": "Créer le rôle",
  "roles.new.nameNeeded": "Donnez un nom au rôle.",
  "roles.new.grantsNeeded": "Choisissez à quoi sert le rôle : un rôle qui n'accorde rien n'a aucun effet.",
  "roles.new.grants": "Ce qu'il peut utiliser",
  "roles.new.permissions": "Ce qu'il peut faire",
  "roles.lock.critical": "Seul un Owner peut l'accorder.",
  "roles.lock.high": "Vous ne pouvez accorder que ce que vous détenez vous-même.",
  "roles.lock.readOnly": "Vous ne pouvez rien accorder sur un rôle.",
  "roles.lock.featureFirst": "Activez d'abord ce sur quoi cela repose.",
  "screen.teams": "Équipes",
  "teams.title": "Équipes",
  "teams.col.name": "Équipe",
  "teams.col.purpose": "Ce qu'elle fait",
  "teams.col.lead": "Dirigée par",
  "teams.col.members": "Personnes",
  "teams.col.status": "Statut",
  "teams.col.person": "Personne",
  "teams.col.roles": "Rôle",
  "teams.col.onTeam": "Dans l'équipe",
  "teams.col.leads": "La dirige",
  "teams.open": "Ouvrir",
  "teams.back": "Toutes les équipes",
  "teams.none": "Aucune équipe pour l'instant.",
  "teams.noMembers": "Personne n'est encore dans cette équipe.",
  "teams.noOthers": "Tout le monde est déjà dans cette équipe.",
  "teams.loading": "Chargement des équipes…",
  "teams.loadFailed": "Impossible de lire les équipes.",
  "teams.retry": "Réessayer",
  "teams.status.open": "Ouverte",
  "teams.status.closed": "Fermée",
  "teams.rename": "Renommer",
  "teams.close": "Fermer l'équipe",
  "teams.reopen": "La rouvrir",
  "teams.closedReason": "Fermée",
  "teams.reopenedReason": "Rouverte",
  "teams.closedNote": "Fermée. Tout le monde y reste : la rouvrir les retrouve tels quels.",
  "teams.new": "Nouvelle équipe",
  "teams.explain":
    "Un groupe de personnes qui font le même travail, avec un responsable. Le travail arrive à l'équipe, pas à une personne : celle qui est de service le prend. Une équipe n'accorde rien.",
  "teams.new.people":
    "Qui en fait partie dès sa création. Cochez « La dirige » pour dire de qui ce sont les personnes : elle réattribue le travail de l'équipe et remplace n'importe qui.",
  "teams.new.name": "Nom",
  "teams.new.purpose": "Ce qu'elle fait",
  "teams.new.create": "Créer l'équipe",
  "teams.new.cancel": "Annuler",
  "teams.new.nameNeeded": "Donnez un nom à l'équipe.",
  "teams.nameTaken": "Une équipe portant ce nom existe déjà.",
  "teams.created": "Équipe créée.",
  "teams.leadHint": "Ce sont ses personnes : elle réattribue le travail de l'équipe et remplace n'importe qui.",
  "teams.leadNeedsMember": "On ne peut diriger qu'une équipe dont on fait partie.",
  "teams.noLead": "Sans responsable",
  "teams.gone": "On ne peut pas mettre dans une équipe une personne partie.",
  "teams.locked": "Vous ne pouvez pas modifier les équipes.",
  "screen.rules": "Règles",
  "screen.hr": "Ressources humaines",
  "sub.hr":
    "Cherchez n'importe qui. Le recrutement, les dossiers et tout ce qui s'y trouve sont aux ressources humaines.",
  "screen.team": "Rôles & équipe",
  "screen.audit": "Audits Walliyo",

  // ── Locked screen (opened by URL without the feature) ──
  "lock.requires": "Cet écran nécessite",
  "lock.or": " ou ",
  "lock.roles": "Vos rôles ({roles}) ne l'incluent pas, donc il n'apparaît pas dans votre menu.",
  "lock.noRoles": "Vous n'avez aucun rôle, donc il n'apparaît pas dans votre menu.",

  // ── Sign-in ──
  "signIn.title": "Connexion à la console",
  "signIn.subtitle": "Réservée à l'équipe Walliyo. Un code vous sera envoyé par SMS.",
  "signIn.email": "Email professionnel",
  "signIn.password": "Mot de passe",
  "signIn.submit": "Continuer",
  "signIn.submitting": "Vérification…",
  "verify.title": "Code de vérification",
  "verify.subtitle": "Nous avons envoyé un code par SMS au {phone}.",
  "verify.code": "Code reçu par SMS",
  "verify.submit": "Se connecter",
  "verify.submitting": "Vérification…",
  "verify.expiresIn": "Le code expire dans {time}.",
  "verify.expired": "Le code a expiré. Recommencez la connexion pour en recevoir un nouveau.",
  "verify.restart": "Recommencer la connexion",

  // ── Errors: what failed and what to do (ADR-0016 §2.5) ──
  "error.requestId": "Identifiant de requête : {id}",
  "error.noRequestId": "Aucun identifiant de requête : le serveur n'a pas répondu.",
  "error.signIn.unauthenticated": "Email ou mot de passe incorrect. Vérifiez-les et réessayez.",
  "error.signIn.validation_error": "Saisissez un email et un mot de passe valides.",
  "error.verify.validation_error": "Saisissez le code à 6 chiffres reçu par SMS.",
  "error.verify.session_expired": "Le code a expiré. Recommencez la connexion pour en recevoir un nouveau.",
  "error.verify.unauthenticated": "Cette demande de code n'est plus valable. Recommencez la connexion.",
  "error.unauthenticated": "Vous n'êtes plus connecté. Reconnectez-vous.",
  "error.session_expired": "Votre session a expiré. Reconnectez-vous.",
  "error.feature_not_enabled": "Vos rôles ne permettent pas cette action. Demandez l'accès à un responsable.",
  "error.invalid_code": "Code incorrect. Vérifiez le SMS et saisissez à nouveau le code.",
  "error.too_many_attempts": "Trop de tentatives. Attendez quelques minutes, puis recommencez la connexion.",
  "error.stale_version": "Ceci a changé pendant que vous le regardiez. Rechargez pour voir la version actuelle.",
  "error.validation_error": "Certaines informations sont invalides. Corrigez-les et réessayez.",
  "error.internal_error":
    "Le serveur a rencontré une erreur. Réessayez ; si cela continue, transmettez l'identifiant ci-dessous à l'équipe technique.",
  "error.network_error":
    "Pas de réponse du serveur après plusieurs tentatives. Vérifiez votre connexion, puis réessayez.",
  "error.network_error.write":
    "Pas de réponse du serveur : la demande est peut-être passée. Réessayez, elle ne sera pas faite deux fois.",
  "error.unexpected_response":
    "Réponse inattendue du serveur. Réessayez ; si cela continue, transmettez l'identifiant ci-dessous à l'équipe technique.",
  "error.unknown":
    "Une erreur inattendue s'est produite dans la console. Rechargez la page ; si cela continue, prévenez l'équipe technique.",

  "sub.cases": "File partagée · appels, emails et chat au même endroit",

  "sub.customers": "Rechercher un client ou un pro et voir tout son compte",

  "sub.verifications": "Vérifier l'identité des pros avant qu'on puisse les trouver",

  "sub.reports": "Signalements sur des pros ou des clients, par gravité",

  "sub.billing": "Prix par pays, promos et remises · un pro garde le prix auquel il s'est abonné",
  "sub.payments": "Frais des pros · Orange Money et carte",

  "sub.features": "Activer ou désactiver par niveau : tout le monde, une région, une version, une personne",

  "sub.rules": "Valeurs de l'entreprise · chaque modification crée une version",

  // ── Fonctionnalités ──
  "features.title": "Activations des fonctionnalités",
  "features.loading": "Chargement des fonctionnalités…",
  "features.loadFailed": "Impossible de charger les fonctionnalités.",
  "features.retry": "Réessayer",
  "features.close": "Fermer",
  "features.pick": "Choisissez une fonctionnalité pour voir ses exceptions.",
  "features.notFound": "Cette fonctionnalité n'existe pas ou plus. Choisissez-en une dans la liste.",
  "features.subject.for": "Fiche",
  "features.subject.allClients": "Tous les clients",
  "features.subject.allPros": "Tous les pros",
  "features.subject.user": "Un utilisateur",
  "features.subject.uuid": "UUID",
  "features.subject.uuidHint": "31290388",
  "features.subject.unknown": "UUID introuvable.",
  "features.kind.client": "Client",
  "features.kind.pro": "Pro",
  "features.col.source": "Source",
  "features.col.value": "Valeur",
  "features.col.category": "Catégorie",
  "features.value.on": "Activé",
  "features.value.off": "Désactivé",
  "features.source.default": "Défaut",
  "features.source.thisUser": "Cet utilisateur",
  "features.source.user": "Un utilisateur",
  "features.source.allClients": "Tous les clients",
  "features.source.allPros": "Tous les pros",
  "features.source.proProfile": "Profil pro",
  "features.source.region": "Région",
  "features.source.appVersion": "Version de l'app",
  "features.source.system": "Système",
  "features.locked.owner": "Seul un Owner peut la modifier",
  "features.locked.scope": "Pas modifiable à ce niveau",
  "features.save.discard": "Annuler",
  "features.save.sending": "Enregistrement…",
  "features.save.nothing": "Rien à enregistrer : aucune modification.",
  "features.save.locked": "Une des modifications ne vous est pas permise.",
  "features.save.reasonNeeded": "Dites pourquoi, en quelques mots.",
  "features.save.leave": "Vous avez des modifications non enregistrées.",
  "features.clear": "Revenir à la valeur héritée",
  "features.cleared": "Sera effacé",
  "features.filter.any": "Tous",
  "features.filter.own": "Définie ici",
  "features.filter.inherited": "Héritée",
  "features.filter.search": "Rechercher",
  "features.filter.none": "Aucune fonctionnalité ne correspond à ces filtres.",
  "features.surface.app": "App Walliyo",
  "features.surface.console": "Console Walliyo",
  "features.save.title.one": "1 modification non enregistrée",
  "features.save.title.many": "{count} modifications non enregistrées",
  "features.save.submit.one": "Enregistrer 1 modification",
  "features.save.submit.many": "Enregistrer {count} modifications",
  "features.save.done.one": "1 modification enregistrée.",
  "features.save.done.many": "{count} modifications enregistrées.",
  "features.unsaved": "non enregistré",
  "features.surface.consoleSoon":
    "Les {count} fonctionnalités de la console ne se règlent encore que par rôle. Le réglage par personne arrive ici.",
  "features.surface.consoleWhere": "Ouvrir Rôles & équipe",
  "features.subject.isStaff":
    "Ce numéro est celui d'un employé Walliyo. Ses fonctionnalités sont dans l'onglet Console Walliyo.",
  "features.subject.search": "Rechercher",
  "features.subject.searching": "Recherche…",
  "features.subject.needsUuid": "Saisissez les huit chiffres de l'UUID.",
  "features.subject.pickFirst": "Choisissez pour qui, puis lancez la recherche.",
  "features.subject.stale":
    "Ces résultats sont ceux de la recherche précédente. Relancez la recherche pour les mettre à jour.",
  "features.pill.allClients": "Toutes les fiches clients",
  "features.pill.allPros": "Toutes les fiches pros",
  "features.pill.unknown": "Utilisateur inconnu",
  "features.pill.staff": "Employé Walliyo",
  "features.subject.allStaff": "Tout le personnel",
  "features.subject.staffMember": "Employé",
  "features.staffSource.override": "Dérogation",
  "features.staffSource.byRole": "Par rôle",
  "features.staffSource.byRoleNamed": "Par rôle : {roles}",
  "features.staffSource.allStaff": "Tout le personnel",
  "features.locked.ownAccount": "Vous ne pouvez pas modifier votre propre compte",
  "features.locked.grant": "Vous ne pouvez pas accorder celle-ci",
  "features.pill.allStaff": "Toutes les fiches du personnel",
  "features.pill.unknownStaff": "Employé inconnu",
  "features.subject.isCustomer":
    "Ce numéro est celui d'un client, pas d'un employé. Ses fonctionnalités sont dans l'onglet App Walliyo.",
  "features.subject.employeeUuid": "UUID de l'employé",
  "features.subject.staffUuidHint": "27217102",
  "features.col.feature": "Fonctionnalité",
  "features.col.default": "Défaut",
  "features.col.exceptions": "Exceptions",
  "features.for.clients": "Pour les clients",
  "features.for.pros": "Pour les pros",
  "features.for.everyone": "Pour tous",
  "features.on": "Activé",
  "features.off": "Désactivé",
  "features.planned": "Prévu",
  "features.planned.note": "Prévue : une valeur peut être définie, mais aucun code ne la lit encore.",
  "features.detail.default": "Par défaut : {value}",
  "features.sensitivity.open":
    "Sensibilité {level} : toute personne qui gère les fonctionnalités de l'app peut la modifier.",
  "features.sensitivity.critical":
    "Sensibilité critique : seul un propriétaire peut la modifier, et les propriétaires sont prévenus.",
  "features.locked.critical":
    "Verrouillé : cette fonctionnalité est critique, seul un propriétaire peut la modifier. Vos rôles ({roles}) n'en font pas un propriétaire ; le serveur refuserait aussi.",
  "features.exceptions.title": "Exceptions par niveau",
  "features.col.scope": "Niveau",
  "features.col.target": "Cible",
  "features.col.reason": "Motif",
  "features.col.expires": "Expire",
  "features.col.actions": "Gérer",
  "features.default.scope": "Défaut",
  "features.default.reason": "valeur par défaut",
  "features.exceptions.none": "Aucune exception : la valeur par défaut s'applique à tout le monde.",
  "features.expired": "Expirée",
  "features.expires.never": "Jusqu'à modification",
  "features.edit": "Modifier",
  "features.remove": "Retirer",
  "features.add": "Ajouter une exception",
  "features.scope.system": "Système",
  "features.scope.allClients": "Tous les clients",
  "features.scope.allPros": "Tous les pros",
  "features.scope.region": "Région",
  "features.scope.appVersion": "Version de l'app",
  "features.scope.user": "Une personne",
  "features.scope.proProfile": "Un profil pro",
  "features.scope.clientProfile": "Un profil client",
  "features.target.everyone": "Tout le monde",
  "features.target.allClients": "Tous les clients",
  "features.target.allPros": "Tous les pros",
  "features.target.appVersion": "Version {version} et plus récentes",
  "features.target.unnamed": "Compte sans nom",
  "features.form.addTitle": "Nouvelle exception",
  "features.form.editTitle": "Modifier l'exception",
  "features.form.scope": "Niveau",
  "features.form.scopePick": "Choisir un niveau…",
  "features.form.scopeUnsupported": "{scope} (pas encore possible)",
  "features.form.clientProfileNote":
    "Un profil client ne peut pas encore recevoir de valeur : les profils clients n'existent pas encore. Pour un client, choisissez « Une personne ».",
  "features.form.needsSearch": "Choisir une personne ou un profil pro nécessite",
  "features.form.needsView": "Trouver le profil pro d'un compte nécessite",
  "features.form.region": "Région",
  "features.form.regionPick": "Choisir une région…",
  "features.form.appVersion": "À partir de la version",
  "features.form.appVersionHint": "Par exemple 2.4.0 : s'applique à cette version et aux plus récentes.",
  "features.form.person": "Personne",
  "features.form.pro": "Profil pro",
  "features.form.search": "Rechercher",
  "features.form.searching": "Recherche…",
  "features.form.searchPlaceholder": "Nom, téléphone, email ou identifiant",
  "features.form.noResults": "Aucun compte ne correspond.",
  "features.form.pickedChange": "Changer",
  "features.form.value": "Valeur",
  "features.form.reason": "Motif (obligatoire)",
  "features.form.reasonHint": "Enregistré dans le journal d'audit avec la modification.",
  "features.form.expires": "Expire le",
  "features.form.expiresHint": "Facultatif. Vide : jusqu'à modification.",
  "features.form.version": "Basée sur la version {version} de cette exception.",
  "features.form.submitAdd": "Ajouter l'exception",
  "features.form.submitEdit": "Enregistrer",
  "features.form.cancel": "Annuler",
  "features.form.sending": "Envoi…",
  "features.problem.scope": "Choisissez un niveau parmi ceux proposés.",
  "features.problem.region": "Choisissez une région.",
  "features.problem.appVersion": "Saisissez une version comme 2.4.0.",
  "features.problem.person": "Recherchez la personne et choisissez-la dans les résultats.",
  "features.problem.pro": "Recherchez le pro et choisissez un compte qui a un profil pro.",
  "features.problem.exists": "Une exception existe déjà pour cette cible : modifiez-la plutôt.",
  "features.problem.reason": "Donnez un motif de quelques mots.",
  "features.problem.expiryInvalid": "Cette date d'expiration n'est pas valide.",
  "features.problem.expiryPast": "La date d'expiration doit être dans le futur, ou laissez-la vide.",
  "features.refused.critical": "Refusé : cette fonctionnalité est critique, seul un propriétaire peut la modifier.",
  "features.refused.scopeNotSupported":
    "Refusé : un profil client ne peut pas encore recevoir de valeur, les profils clients n'existent pas encore. Choisissez « Une personne ».",
  "features.refused.scopeNotAllowed":
    "Refusé : cette fonctionnalité ne peut pas être réglée à ce niveau. Choisissez un des niveaux proposés.",
  "features.refused.expiryInPast":
    "Refusé : la date d'expiration est passée. Choisissez une date plus tardive, ou laissez-la vide.",
  "features.refused.targetNotFound": "Refusé : ce compte ou ce profil pro n'existe plus. Recherchez-le à nouveau.",
  "features.stale":
    "Quelqu'un a modifié cette fonctionnalité entre-temps. Rechargez pour voir ses exceptions actuelles, puis recommencez.",
  "features.done.added": "Exception ajoutée.",
  "features.done.saved": "Exception modifiée.",
  "features.done.removed": "Exception retirée : le niveau plus large, ou la valeur par défaut, s'applique de nouveau.",
  "features.remove.title": "Retirer l'exception",
  "features.remove.body":
    "Retirer l'exception « {scope} · {target} : {value} » de {feature}. Le niveau plus large, ou la valeur par défaut, s'appliquera de nouveau.",
  "features.remove.submit": "Retirer",

  // ── Règles ──
  "rules.loading": "Chargement des règles…",
  "rules.loadFailed": "Impossible de charger les règles.",
  "rules.retry": "Réessayer",
  "rules.pick": "Choisissez une règle pour voir son historique.",
  "rules.notFound": "Cette règle n'existe pas. Choisissez-en une dans la liste.",
  "rules.next": "à partir du {date} : {value}",
  "rules.now": "En vigueur",
  "rules.default": "valeur par défaut",
  "rules.kind.gnf": "montant en GNF",
  "rules.kind.minutes": "durée en minutes",
  "rules.kind.hours": "durée en heures",
  "rules.kind.days": "durée en jours",
  "rules.kind.km": "distance en km",
  "rules.kind.number": "nombre",
  "rules.kind.boolean": "oui ou non",
  "rules.kind.choice": "choix dans une liste",
  "rules.kind.text": "texte",
  "rules.sensitivity.open": "Sensibilité {level} : toute personne qui peut modifier les règles peut créer une version.",
  "rules.sensitivity.critical": "Sensibilité critique : seul un propriétaire peut créer une version.",
  "rules.legalBasis.required": "Chaque changement doit citer sa base légale.",
  "rules.locked.critical":
    "Verrouillé : cette règle est critique, seul un propriétaire peut créer une version. Vos rôles ({roles}) n'en font pas un propriétaire ; le serveur refuserait aussi.",
  "rules.locked.retired": "Cette règle est retirée : plus rien ne la lit, elle ne peut plus changer.",
  "rules.history": "Historique des versions",
  "rules.history.none": "Aucune version : la valeur par défaut ({value}) s'applique.",
  "rules.col.version": "N° de version",
  "rules.col.value": "Valeur",
  "rules.col.effective": "En vigueur",
  "rules.col.reason": "Motif",
  "rules.col.legalBasis": "Base légale",
  "rules.col.by": "Par",
  "rules.status.scheduled": "À partir du {date}",
  "rules.status.current": "Actuelle",
  "rules.status.since": "depuis le {date}",
  "rules.status.past": "Remplacée",
  "rules.new": "Nouvelle version",
  "rules.form.title": "Nouvelle version",
  "rules.form.value": "Nouvelle valeur",
  "rules.form.value.gnf": "Nouveau montant (GNF)",
  "rules.form.value.minutes": "Nouvelle valeur (en minutes)",
  "rules.form.value.hours": "Nouvelle valeur (en heures)",
  "rules.form.value.days": "Nouvelle valeur (en jours)",
  "rules.form.effective": "Entrée en vigueur",
  "rules.form.now": "Dès maintenant",
  "rules.form.at": "À une date et une heure",
  "rules.form.atLabel": "En vigueur le",
  "rules.form.reason": "Motif (obligatoire)",
  "rules.form.legalBasis": "Base légale (obligatoire)",
  "rules.form.legalBasisOptional": "Base légale (si applicable)",
  "rules.form.legalBasisPlaceholder": "Loi, contrat, avis juridique",
  "rules.form.version":
    "Basée sur la version {version} de la règle : si une autre version est ajoutée entre-temps, l'envoi sera refusé.",
  "rules.form.submit": "Créer la version",
  "rules.form.cancel": "Annuler",
  "rules.form.sending": "Envoi…",
  "rules.value.true": "Oui",
  "rules.value.false": "Non",
  "rules.problem.value": "Saisissez une valeur.",
  "rules.problem.number": "Saisissez un nombre, par exemple 750000.",
  "rules.problem.integer": "Saisissez un nombre entier.",
  "rules.problem.min": "La valeur doit être d'au moins {min}.",
  "rules.problem.max": "La valeur doit être d'au plus {max}.",
  "rules.problem.choice": "Choisissez une valeur dans la liste.",
  "rules.problem.tooLong": "{max} caractères au plus.",
  "rules.problem.effectiveMissing": "Choisissez la date et l'heure d'entrée en vigueur.",
  "rules.problem.effectivePast":
    "Une version ne peut pas prendre effet dans le passé : ce qui s'appliquait alors reste au dossier. Choisissez maintenant ou plus tard.",
  "rules.problem.reason": "Donnez un motif de quelques mots.",
  "rules.problem.legalBasis": "Citez la loi, le contrat ou l'avis juridique derrière ce changement.",
  "rules.refused.critical": "Refusé : cette règle est critique, seul un propriétaire peut créer une version.",
  "rules.refused.effectivePast":
    "Refusé : cette date d'entrée en vigueur est passée. Choisissez maintenant ou plus tard.",
  "rules.refused.legalBasis":
    "Refusé : cette règle exige une base légale. Citez la loi, le contrat ou l'avis juridique.",
  "rules.refused.invalidValue": "Refusé : cette valeur ne convient pas à la règle. Vérifiez l'unité et le minimum.",
  "rules.refused.retired": "Refusé : cette règle est retirée, plus rien ne la lit.",
  "rules.stale":
    "Une autre version de cette règle a été ajoutée entre-temps. Rechargez pour la voir, puis recommencez.",
  "rules.done.now": "Version {version} créée : elle s'applique dès maintenant.",
  "rules.done.scheduled": "Version {version} créée : elle s'appliquera à partir du {date}.",

  "sub.team": "Chaque rôle est une liste de fonctionnalités. Personne ne peut s'accorder quoi que ce soit à soi-même.",

  "sub.audit": "Tout ce qui a été fait, par qui, pour qui · votre consultation est elle-même enregistrée",

  // ── Journal d'audit ──
  "audit.filter.subject": "Personne concernée",
  "audit.filter.subjectPlaceholder": "Identifiant du compte",
  "audit.filter.staff": "Membre de l'équipe",
  "audit.filter.staffPlaceholder": "Identifiant du membre",
  "audit.filter.action": "Action",
  "audit.filter.area": "Domaine",
  "audit.filter.outcome": "Résultat",
  "audit.filter.from": "Du",
  "audit.filter.to": "Au (inclus)",
  "audit.filter.all": "Tous",
  "audit.filter.allActions": "Toutes",
  "audit.filter.clear": "Effacer les filtres",
  "audit.problem.id": "Un identifiant a la forme 3f2a…-…-…-…-… (UUID). Copiez-le depuis la fiche du compte.",
  "audit.problem.period": "La fin de la période est avant son début.",
  "audit.problem.needsPerson": "Indiquez l'UUID de l'auteur, celui de la personne, ou les deux.",
  "audit.filtersAria": "Filtres du journal",
  "audit.searchAria": "Recherche dans le journal",
  "audit.scope.user": "Individu",
  "audit.scope.allStaff": "Tout le personnel",
  "audit.scope.allClients": "Tous les clients",
  "audit.scope.allPros": "Tous les pros",
  "audit.scope.staffRole": "Rôle",
  "audit.scope.proProfile": "Profil pro",
  "audit.scope.region": "Région",
  "audit.scope.appVersion": "Version de l'app",
  "audit.scope.system": "Système",
  "audit.scope.staffMember": "Individu",
  "audit.filter.involving": "Concerne",
  "audit.filter.updaterUuid": "UUID de l'auteur",
  "audit.filter.userUuid": "UUID de la personne",
  "audit.pill.updater": "Auteur",
  "audit.pill.user": "Personne",
  "audit.col.time": "Heure",
  "audit.col.actor": "Auteur",
  "audit.col.action": "Réglage",
  "audit.col.subject": "Concerne",
  "audit.col.outcome": "Résultat",
  "audit.col.reason": "Motif",
  "audit.filter.effect": "Afficher",
  "audit.effect.write": "Modifications",
  "audit.effect.read": "Consultations",
  "audit.col.before": "Avant",
  "audit.col.after": "Après",
  "audit.change.arrow": "{before} → {after}",
  "audit.change.field": "{key} : {value}",
  "audit.outcome.succeeded": "Réussi",
  "audit.outcome.denied": "Refusé",
  "audit.outcome.requested": "Demandé",
  "audit.actor.staff": "Membre de l'équipe",
  "audit.actor.noRoles": "aucun rôle",
  "audit.actor.customer": "client",
  "audit.actor.self": "client (lui-même)",
  "audit.actor.system": "Système",
  "audit.subject.onBehalf": "pour le compte de {id}",
  "audit.filterBy": "Filtrer sur {id}",
  "audit.subject.open": "Ouvrir le compte de {name}",
  "audit.masked": "masqué",
  "audit.sensitive": "🔒 sensible",
  "audit.loading": "Chargement du journal…",
  "audit.loadFailed": "Le journal n'a pas pu être chargé.",
  "audit.retry": "Réessayer",
  "audit.empty": "Aucune entrée ne correspond à ces filtres.",
  "audit.emptyForPerson": "Aucune entrée pour cette recherche.",
  "audit.fixProblems": "Corrigez les filtres signalés pour afficher le journal.",
  "audit.loadMore": "Charger plus",
  "audit.loadingMore": "Chargement…",
  "audit.moreFailed": "La page suivante n'a pas pu être chargée. Les entrées déjà affichées restent valables.",
  "audit.count": "{count} entrées affichées",
  "audit.countOne": "1 entrée affichée",
  "audit.end": "Fin du journal pour ces filtres.",
  "audit.recorded":
    "Votre consultation est elle-même enregistrée (VIEW_AUDIT_LOG_STAFF_ACTION), avec les filtres utilisés.",
  "audit.sensitiveShown": "Vous voyez les valeurs sensibles (VIEW_SENSITIVE_AUDIT_VALUES).",
  "audit.sensitiveHidden":
    "Les valeurs sensibles sont masquées : il faut VIEW_SENSITIVE_AUDIT_VALUES, accordé par le propriétaire uniquement.",
  "audit.deniedNote": "Une ligne « Refusé » montre qu'une tentative sans droit est aussi enregistrée.",
  "area.account": "Compte",
  "area.access": "Accès",
  "area.onboarding": "Inscription pro",
  "area.discovery": "Recherche",
  "area.jobs": "Demandes",
  "area.leads": "Opportunités",
  "area.messaging": "Messages",
  "area.reviews": "Avis",
  "area.payments": "Paiements",
  "area.support": "Dossiers",
  "area.safety": "Sécurité",
  "area.settings": "Réglages",
  "area.staff": "Équipe",
  "area.audit": "Audit",

  // ── Rôles & équipe ──
  "team.members": "Équipe",
  "team.col.name": "Nom",
  "team.col.roles": "Rôles",
  "team.col.status": "Statut",
  "team.col.lastSeen": "Dernière connexion",
  "team.you": "vous",
  "team.noRoles": "Aucun rôle",
  "team.status.active": "Actif",
  "team.status.invited": "Invité",
  "team.status.deactivated": "Désactivé",
  "team.sessions": "{count} sessions ouvertes",
  "team.sessionsOne": "1 session ouverte",
  "team.lastSeen.never": "Jamais connecté",
  "team.lastSeen.today": "aujourd'hui à {time}",
  "team.lastSeen.yesterday": "hier à {time}",
  "team.lastSeen.date": "le {date}",
  "team.invite": "Inviter",
  "team.manageRoles": "Rôles",
  "team.endSessions": "Couper la session",
  "team.deactivate": "Désactiver",
  "team.loading": "Chargement de l'équipe…",
  "team.loadFailed": "L'équipe n'a pas pu être chargée.",
  "team.retry": "Réessayer",
  "team.emptyStaff": "Personne dans l'équipe pour l'instant.",
  "team.grid": "Rôles × fonctionnalités",
  "team.grid.feature": "Fonctionnalité",
  "team.grid.editable": "Cliquez pour accorder ou retirer · « critical » exige le propriétaire",
  "team.grid.readOnly": "🔒 Lecture seule · modifier nécessite MANAGE_ROLES ou ASSIGN_ROLES",
  "team.grid.members": "{count} membres",
  "team.grid.membersOne": "1 membre",
  "team.grid.yourRole": "🔒 votre rôle",
  "team.grid.newRole": "Nouveau rôle",
  "team.grid.editRole": "Modifier",
  "team.grid.noRoles": "Aucun rôle n'existe encore.",
  "team.lock.ownRole": "Vous avez ce rôle : personne ne modifie ce que son propre rôle accorde.",
  "team.lock.critical": "Critique : seul le propriétaire peut l'accorder ou le retirer.",
  "team.lock.high": "Sensibilité haute : il faut MANAGE_ROLES et avoir soi-même cette fonctionnalité.",
  "team.lock.low": "Il faut MANAGE_ROLES ou ASSIGN_ROLES.",
  "team.lock.criticalShort": "🔒 propriétaire",
  "team.lock.highShort": "🔒 hors de vos droits",
  "team.cell.grant": "Accorder {feature} au rôle {role}",
  "team.cell.revoke": "Retirer {feature} du rôle {role}",
  "team.sensitivity.low": "low",
  "team.sensitivity.high": "high",
  "team.sensitivity.critical": "critical",

  "team.reason": "Motif",
  "team.reasonHint": "Enregistré dans le journal d'audit. Quelques mots suffisent.",
  "team.reasonTooShort": "Donnez un motif d'au moins 3 caractères.",
  "team.cancel": "Annuler",
  "team.close": "Fermer",
  "team.sending": "Envoi…",
  "team.reload": "Recharger",
  "team.refused": "Refusé : {message}",
  "team.stale": "Quelqu'un a modifié ceci entre-temps. Rechargez pour voir la version actuelle, puis recommencez.",

  "team.grant.title": "Accorder une fonctionnalité",
  "team.revoke.title": "Retirer une fonctionnalité",
  "team.grant.body": "Accorder {feature} au rôle {role} ({members}).",
  "team.revoke.body": "Retirer {feature} du rôle {role} ({members}).",
  "team.grant.submit": "Accorder",
  "team.revoke.submit": "Retirer",
  "team.grant.done": "{feature} accordé au rôle {role}.",
  "team.revoke.done": "{feature} retiré du rôle {role}.",
  "team.critical.note": "Fonctionnalité critique : les propriétaires seront prévenus.",

  "team.invite.title": "Inviter un membre de l'équipe",
  "team.invite.name": "Nom complet",
  "team.invite.email": "Email professionnel",
  "team.invite.phone": "Téléphone",
  "team.invite.phoneHint": "Format international, par ex. +224621234567.",
  "team.invite.roles": "Rôles",
  "team.invite.noRoles": "Créez d'abord un rôle — vous pouvez l'inviter maintenant et lui donner des rôles ensuite.",
  "team.invite.rolesHint": "Sans rôle, la console qu'il ou elle ouvrira sera vide.",
  "team.invite.sms":
    "Un code à 6 chiffres sera envoyé par SMS à ce numéro. Valable {hours} heures, il permet de choisir son mot de passe sur la page « Choisir mon mot de passe ».",
  "team.invite.submit": "Envoyer l'invitation",
  "team.invite.done": "{name} est invité. Code envoyé par SMS, valable jusqu'au {until}.",
  "team.invite.missing": "Renseignez le nom, l'email et le téléphone.",

  "team.deactivate.title": "Désactiver {name}",
  "team.deactivate.body":
    "{name} ne pourra plus jamais se connecter. Ses sessions ouvertes et son invitation éventuelle prennent fin immédiatement. Un membre désactivé n'est pas supprimé.",
  "team.deactivate.submit": "Désactiver",
  "team.deactivate.done": "{name} est désactivé. Sessions coupées : {count}.",
  "team.reactivate": "Réactiver",
  "team.reactivate.title": "Réactiver {name}",
  "team.reactivate.body":
    "{name} pourra se reconnecter, avec les rôles qu'il ou elle avait déjà. La connexion repart de zéro : aucune session ni aucun code n'est rétabli.",
  "team.reactivate.submit": "Réactiver",
  "team.reactivate.done": "{name} est de nouveau actif.",

  "team.endSessions.title": "Couper les sessions de {name}",
  "team.endSessions.body": "{name} sera déconnecté partout ({sessions}).",
  "team.endSessions.self": "Ce sont vos propres sessions : vous serez déconnecté aussi.",
  "team.endSessions.submit": "Couper",
  "team.endSessions.done": "Sessions de {name} coupées : {count}.",

  "team.roles.title": "Rôles de {name}",
  "team.roles.held": "Rôles actuels",
  "team.roles.available": "Autres rôles",
  "team.roles.none": "Aucun",
  "team.roles.assign": "Attribuer {role}",
  "team.roles.remove": "Retirer {role}",
  "team.roles.assignDone": "{role} attribué à {name}.",
  "team.roles.removeDone": "{role} retiré à {name}.",
  "team.roles.reasonFirst": "Donnez d'abord un motif, puis choisissez le rôle à attribuer ou retirer.",

  "team.role.createTitle": "Nouveau rôle",
  "team.role.editTitle": "Modifier le rôle {name}",
  "team.role.name": "Nom du rôle",
  "team.role.description": "Description",
  "team.role.descriptionHint": "Facultatif. Ce que fait ce rôle, en une phrase.",
  "team.role.department": "Département",
  "team.role.noDepartment": "Aucun",
  "team.role.departmentHint":
    "L'espace où arrivent ses titulaires. Ce que le rôle permet reste décidé par les fonctionnalités.",
  "team.role.create": "Créer le rôle",
  "team.role.save": "Enregistrer",
  "team.role.created": "Rôle {name} créé. Accordez-lui des fonctionnalités dans la grille.",
  "team.role.saved": "Rôle {name} enregistré.",
  "team.role.nameTooShort": "Le nom fait au moins 2 caractères.",
  "team.role.unchanged": "Modifiez le nom ou la description.",
  "team.role.version": "Version {version}",

  // ── Permissions on a role (ADR-0019) and who can do this ──
  "team.tab.roles": "Rôles & équipe",
  "team.tab.who": "Qui peut le faire",
  "team.tabs.aria": "Sections de l'équipe",
  "team.permission.noLimit": "Aucun plafond",
  "team.permission.lockFeature":
    "Accordez d'abord {feature} au rôle {role} : une permission ne compte que sous sa fonctionnalité.",
  "team.cell.grantPermission": "Accorder {permission} au rôle {role}",
  "team.cell.editPermission": "Modifier {permission} pour le rôle {role}",
  "team.permission.grantTitle": "Accorder une permission",
  "team.permission.editTitle": "Modifier une permission",
  "team.permission.grantBody": "Accorder {permission} au rôle {role} ({members}).",
  "team.permission.editBody": "Le rôle {role} ({members}) a {permission}.",
  "team.permission.under": "Sous {feature}.",
  "team.permission.approvable": "Sans elle, leur tentative devient une demande d'accord.",
  "team.permission.refusable": "Sans elle, l'action est refusée.",
  "team.permission.limits": "Plafonds",
  "team.permission.limitsHint":
    "Le maximum que ses titulaires peuvent faire seuls dans chaque devise. Au-delà, ou dans une devise sans plafond, cela devient une demande d'accord.",
  "team.permission.noLimitHint": "Aucun plafond : ses titulaires agissent seuls, quel que soit le montant.",
  "team.permission.currency": "Devise",
  "team.permission.amount": "Montant",
  "team.permission.addLimit": "Ajouter un plafond dans une autre devise",
  "team.permission.removeLimit": "Retirer le plafond en {currency}",
  "team.permission.limitProblem":
    "Donnez une devise de trois lettres et un montant supérieur à zéro pour chaque plafond.",
  "team.permission.duplicateCurrency": "Un plafond par devise : {currency} est indiquée deux fois.",
  "team.permission.grantDone": "{permission} accordée au rôle {role}.",
  "team.permission.revokeDone": "{permission} retirée au rôle {role}.",
  "team.revoke.permissionsToo": "Ses permissions partent avec elle : {permissions}.",
  "team.refused.sensitivity":
    "Votre accès ne permet pas de l'accorder ni de le retirer. Demandez à un Ops Manager ou au propriétaire.",
  "team.refused.critical": "Critique : seul un propriétaire qui l'a lui-même l'accorde ou le retire.",
  "team.refused.limitAboveOwn":
    "Vous ne pouvez pas accorder un plafond plus élevé que le vôtre. Demandez au propriétaire.",
  "team.refused.featureFirst":
    "Cette permission repose sur une fonctionnalité que le rôle n'accorde pas. Accordez d'abord la fonctionnalité.",
  "team.refused.notLimited": "Cette permission ne porte aucun plafond.",
  "team.refused.limitWithoutGrant": "Un plafond va avec une permission accordée.",
  "team.refused.ownRole":
    "Vous avez ce rôle : vous ne pouvez pas modifier ce qu'il accorde. Demandez à un autre propriétaire.",
  "team.who.action": "Action",
  "team.who.feature": "Fonctionnalité",
  "team.who.permission": "Permission",
  "team.who.noPermission": "Aucune permission ne couvre cette action : qui a la fonctionnalité peut la prendre.",
  "team.who.approvable": "peut être demandée",
  "team.who.col.through": "Par",
  "team.who.col.limit": "Plafond",
  "team.who.personal": "Défini pour cette personne",
  "team.who.mayRequest": "Peut seulement demander : devient une demande d'accord",
  "team.who.loading": "Chargement de qui peut le faire…",
  "team.who.loadFailed": "Impossible de charger qui peut le faire.",
  "team.who.empty": "Personne ne peut le faire.",

  // ── Clients & pros ──
  // Trades, by WorkerCategory.
  "trade.electrician": "Électricien",
  "trade.plumber": "Plombier",
  "trade.mason": "Maçon",
  "trade.carpenter": "Menuisier",
  "trade.ac_technician": "Technicien clim",
  "trade.cleaner": "Femme de ménage",
  "trade.painter": "Peintre",
  "trade.handyman": "Homme à tout faire",
  "trade.laundry": "Blanchisserie",
  "trade.babysitter": "Baby-sitter",
  "trade.fridge_repair": "Réparateur de frigo",
  "trade.moto_driver": "Moto-taxi",
  "trade.tailor": "Tailleur",
  "trade.car_mechanic": "Mécanicien auto",
  "trade.welder": "Soudeur",
  "trade.gardener": "Jardinier",
  "trade.phone_repair": "Réparateur de téléphone",
  "trade.tutor": "Répétiteur",
  "trade.hairdresser": "Coiffeur",
  "trade.security_guard": "Gardien",
  "trade.other": "Autre",
  // A number with its unit (the settings' Unit).
  "unit.km": "{value} km",
  "unit.minutes": "{value} min",
  "unit.hours": "{value} h",
  "unit.days": "{value} j",
  "unit.seconds": "{value} s",

  "customers.search.label": "Rechercher un client ou un pro",
  "customers.search.placeholder": "Nom, téléphone, email ou identifiant du compte",
  "customers.search.hint": "Par ex. « 621 23 45 67 », « Sékou Touré », « mariama.bah@ » ou l'identifiant du compte.",
  "customers.search.submit": "Rechercher",
  "customers.search.tooShort": "Saisissez au moins 2 caractères.",
  "customers.search.locked": "La recherche nécessite",
  "customers.search.idle": "Saisissez un nom, un numéro, un email ou un identifiant pour trouver un compte.",
  "customers.search.loading": "Recherche…",
  "customers.search.loadFailed": "La recherche n'a pas pu aboutir.",
  "customers.search.empty": "Aucun compte ne correspond à « {query} ».",
  "customers.search.matchedBy.userId": "Recherche par identifiant de compte",
  "customers.search.matchedBy.email": "Recherche par email",
  "customers.search.matchedBy.phone": "Recherche par téléphone",
  "customers.search.matchedByPhone": "Recherche par téléphone : {phone}",
  "customers.search.matchedBy.name": "Recherche par nom",
  "customers.search.count": "{count} comptes affichés",
  "customers.search.countOne": "1 compte affiché",
  "customers.search.end": "Fin des résultats.",
  "customers.search.loadMore": "Charger plus",
  "customers.search.loadingMore": "Chargement…",
  "customers.search.moreFailed":
    "La page suivante n'a pas pu être chargée. Les comptes déjà affichés restent valables.",
  "customers.search.retry": "Réessayer",
  "customers.search.masked":
    "Numéros et emails masqués. « Afficher », sur la page du compte et avec un motif, est enregistré.",
  "customers.col.name": "Nom",
  "customers.col.contact": "Contact",
  "customers.col.kind": "Client ou pro",
  "customers.col.region": "Région",
  "customers.col.since": "Membre depuis",
  "customers.deleted": "Compte supprimé",
  "customers.deletedName": "Nom effacé",
  "customers.kind.client": "Client",
  "customers.kind.pro": "Pro",
  "customers.kind.proAndClient": "Pro et client",
  "customers.kind.admin": "Administrateur",
  "customers.region.gn": "Guinée",
  "customers.region.sn": "Sénégal",
  "customers.region.us": "États-Unis",
  "customers.region.eu": "Europe",
  "customers.verification.unverified": "Non vérifiée",
  "customers.verification.pending": "En attente",
  "customers.verification.verified": "Vérifiée",
  "customers.verification.rejected": "Refusée",

  "customers.back": "← Recherche",
  "customers.page.loading": "Chargement du compte…",
  "customers.page.loadFailed": "Le compte n'a pas pu être chargé.",
  "customers.page.notFound": "Aucun compte n'a cet identifiant. Recherchez la personne à nouveau.",
  "customers.page.locked": "La page d'un compte nécessite",
  "customers.page.sub": "{kind} · membre depuis {since} · {region}",
  "customers.page.deletedTitle": "Compte supprimé",
  "customers.page.deletedSub": "Supprimé le {date} · nom et coordonnées effacés",

  "customers.reveal.button": "Afficher",
  "customers.reveal.locked": "🔒 masqué",
  "customers.reveal.lockedTitle": "Afficher les coordonnées nécessite REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
  "customers.reveal.title": "Afficher les coordonnées de {name}",
  "customers.reveal.body":
    "Le numéro et l'email complets s'affichent sur cette page seulement, le temps de votre consultation. L'affichage est enregistré dans le journal du compte, avec votre motif.",
  "customers.reveal.reason": "Motif",
  "customers.reveal.placeholder": "Pourquoi ? ex. rappeler le client, dossier D-0142",
  "customers.reveal.tooShort": "Donnez un motif d'au moins 3 caractères.",
  "customers.reveal.submit": "Afficher",
  "customers.reveal.sending": "Envoi…",
  "customers.reveal.cancel": "Annuler",
  "customers.reveal.noAnswer":
    "Pas de réponse du serveur : l'affichage a peut-être été enregistré sans que les coordonnées vous parviennent. Réessayez ; un nouvel affichage sera enregistré lui aussi.",
  "customers.reveal.recorded": "Affiché à {time} · consultation enregistrée dans le journal du compte.",
  "customers.reveal.hide": "Masquer",
  "customers.reveal.phone": "Téléphone :",
  "customers.reveal.email": "Email :",
  "customers.reveal.none": "aucun",

  "customers.obo.title": "Agir pour le compte de {name}",
  "customers.obo.body":
    "Un code est envoyé par SMS au numéro du compte. Le client le lit à voix haute ; la session dure 30 minutes et chaque action est enregistrée avec son nom et le vôtre.",
  "customers.obo.send": "Envoyer le code",
  "customers.obo.soon": "Bientôt disponible",

  "customers.tabs.aria": "Sections du compte",
  "customers.tab.overview": "Aperçu",
  "customers.tab.subscription": "Abonnement",
  "customers.tab.settings": "Fonctionnalités & réglages",
  "customers.tab.jobs": "Demandes",
  "customers.tab.audit": "Journal",

  "customers.pro.title": "Profil pro",
  "customers.pro.none": "Pas de profil pro.",
  "customers.pro.identity": "Identité",
  "customers.pro.approval": "Approbation",
  "customers.pro.rating": "Note",
  "customers.pro.ratingValue": "{rating} ★ · {count} avis",
  "customers.pro.noReviews": "Aucun avis",
  "customers.pro.rate": "Tarif",
  "customers.pro.subscription": "Forfait",
  "customers.pro.noSubscription": "Aucune",
  "customers.pro.subscriptionLine": "{plan} · {status}",
  "customers.pro.paidUntil": "{plan} · payée au {date}",
  "customers.pro.endingUntil": "{plan} · active jusqu'au {date}, sans renouvellement",
  "customers.pro.approval.pending": "En attente",
  "customers.pro.approval.approved": "Approuvé",
  "customers.pro.approval.rejected": "Refusé",
  "customers.subscription.pending": "en attente",
  "customers.subscription.active": "active",
  "customers.subscription.ending": "sans renouvellement",
  "customers.subscription.suspended": "suspendue",
  "customers.subscription.ended": "terminée",
  "customers.rate.hourly": "{amount} / heure",
  "customers.rate.daily": "{amount} / jour",
  "customers.rate.fixed": "{amount} forfait",
  "customers.client.title": "Profil client",
  "customers.client.jobsPosted": "Demandes publiées",
  "customers.client.lastActivity": "Dernière activité",
  "customers.client.never": "Aucune",
  "customers.restrictions.title": "Restrictions actives",
  "customers.restrictions.none": "Aucune restriction.",
  "customers.restrictions.suspension": "Suspension provisoire",
  "customers.restrictions.line": "{feature} désactivé pour {scope} · motif : {reason}",
  "customers.restrictions.until": "jusqu'au {date}",

  "customers.settings.col.setting": "Réglage",
  "customers.settings.col.value": "Valeur",
  "customers.settings.col.source": "Niveau",
  "customers.settings.col.reason": "Motif",
  "customers.settings.col.expires": "Expire",
  "customers.settings.on": "Activé",
  "customers.settings.off": "Désactivé",
  "customers.settings.restricted": "Restreint",
  "customers.settings.none": "Aucun réglage ne s'applique à ce compte.",
  "customers.settings.edit": "Modifier un réglage",
  "customers.settings.editSoon":
    "Bientôt disponible : la modification des réglages d'un client arrive dans une prochaine version.",
  "customers.settings.foot":
    "Le niveau le plus précis l'emporte : personne, profil pro, tous les pros ou tous les clients, région, système, puis la valeur par défaut.",
  "customers.source.user": "Personne",
  "customers.source.proProfile": "Profil pro",
  "customers.source.allPros": "Tous les pros",
  "customers.source.allClients": "Tous les clients",
  "customers.source.region": "Région",
  "customers.source.regionOf": "Région {region}",
  "customers.source.appVersion": "Version de l'app",
  "customers.source.system": "Système",
  "customers.source.default": "Défaut",
  "customers.scope.user": "la personne",
  "customers.scope.proProfile": "le profil pro",

  "customers.jobs.col.trade": "Métier",
  "customers.jobs.col.type": "Type",
  "customers.jobs.col.side": "Rôle",
  "customers.jobs.col.other": "Avec",
  "customers.jobs.col.status": "Statut",
  "customers.jobs.col.date": "Date",
  "customers.jobs.type.direct": "Directe",
  "customers.jobs.type.open": "Ouverte",
  "customers.jobs.side.client": "Client",
  "customers.jobs.side.pro": "Pro",
  "customers.jobs.status.requested": "Demandée",
  "customers.jobs.status.quoted": "Devis envoyé",
  "customers.jobs.status.accepted": "Présentation faite",
  "customers.jobs.status.cancelled": "Annulée",
  "customers.jobs.noPro": "Aucun pro choisi",
  "customers.jobs.deletedParty": "Compte supprimé",
  "customers.jobs.none": "Aucune demande pour ce compte.",
  "customers.jobs.foot": "Les {count} dernières demandes au plus, comme client ou comme pro.",

  "customers.journal.recorded": "Votre consultation de ce journal est elle-même enregistrée.",
  "customers.journal.locked": "Le journal d'audit nécessite",
  "customers.lockedRoles": "Vos rôles ({roles}) ne l'incluent pas.",
  "customers.journal.onBehalf": "pour le compte de {id}",
  "customers.journal.actorWithRoles": "{name} ({roles})",
  "customers.journal.reason": "motif : {reason}",
  "customers.journal.empty": "Rien n'est encore enregistré pour ce compte.",
  "customers.journal.all": "Ouvrir dans le journal d'audit →",
  "customers.journal.denied": "refusé",
  "customers.journal.succeeded": "réussi",

  // ── Choisir mon mot de passe (public) ──
  "setPassword.title": "Choisir mon mot de passe",
  "setPassword.subtitle": "Utilisez le code à 6 chiffres reçu par SMS avec votre invitation.",
  "setPassword.email": "Email professionnel",
  "setPassword.code": "Code reçu par SMS",
  "setPassword.password": "Nouveau mot de passe",
  "setPassword.confirm": "Confirmez le mot de passe",
  "setPassword.rules": "Le mot de passe doit :",
  "setPassword.rule.length": "faire au moins {min} caractères",
  "setPassword.rule.tooLong": "faire au plus {max} caractères",
  "setPassword.rule.mix": "contenir une lettre et un caractère qui n'est pas une lettre",
  "setPassword.rule.email": "ne pas contenir votre email ({name})",
  "setPassword.rule.emailGeneric": "ne pas contenir votre email",
  "setPassword.mismatch": "Les deux mots de passe ne sont pas identiques.",
  "setPassword.codeFormat": "Le code est composé de 6 chiffres.",
  "setPassword.emailFormat": "Saisissez un email valide.",
  "setPassword.submit": "Enregistrer mon mot de passe",
  "setPassword.submitting": "Enregistrement…",
  "setPassword.back": "Retour à la connexion",
  "setPassword.done": "Mot de passe enregistré. Connectez-vous avec votre email et ce mot de passe.",
  "signIn.setPassword": "Invité ? Choisir mon mot de passe",
  "signIn.or": "ou",
  "signIn.heading": "Console Walliyo",
  "signIn.forgot": "Mot de passe oublié ?",
  "signIn.environment": "Environnement",
  "signIn.build": "Version",
  "signIn.googleUnavailable":
    "La connexion Google n'est pas disponible. Utilisez votre e-mail et votre mot de passe, ou prévenez le propriétaire.",
  "error.setPassword.invalid_code":
    "Cet email et ce code ne correspondent à aucune invitation ouverte. Vérifiez les deux ; si le code est perdu ou expiré, demandez à être invité à nouveau.",
  "error.setPassword.too_many_attempts":
    "Trop de codes incorrects : ce code n'est plus valable. Demandez à être invité à nouveau.",
  "error.setPassword.validation_error":
    "Le mot de passe ne respecte pas les règles, ou le code n'a pas le bon format. Corrigez et réessayez.",

  // ── Dossiers (the support case queue) ──
  "cases.newCall": "+ Noter un appel",
  "cases.queue.aria": "File des dossiers",
  "cases.queue.views": "Vues de la file",
  "cases.view.all": "Tous",
  "cases.view.unassigned": "Non attribués",
  "cases.view.mine": "Les miens",
  "cases.view.late": "En retard",
  "cases.filter.status": "Statut",
  "cases.filter.notClosed": "Tous sauf clos",
  "cases.filter.channel": "Canal",
  "cases.filter.anyChannel": "Tous les canaux",
  "cases.filter.customer": "Client {id}",
  "cases.filter.clear": "Retirer le filtre client",
  "cases.col.case": "Dossier",
  "cases.col.deadline": "Délai",
  "cases.queue.loading": "Chargement de la file…",
  "cases.queue.loadFailed": "La file n'a pas pu être chargée.",
  "cases.queue.empty": "Aucun dossier ne correspond à ces filtres.",
  "cases.loadMore": "Charger plus",
  "cases.loadingMore": "Chargement…",
  "cases.retry": "Réessayer",
  "cases.pick": "Choisissez un dossier dans la file pour voir sa chronologie.",
  "cases.loading": "Chargement du dossier…",
  "cases.loadFailed": "Ce dossier n'a pas pu être chargé.",
  "cases.notFound": "Aucun dossier n'a cet identifiant. Il a peut-être été mal copié.",
  "cases.refreshFailed": "Le dossier n'a pas pu être actualisé : ce qui s'affiche peut être dépassé.",
  "cases.deadline.late": "En retard · {time}",
  "cases.deadline.left": "{time} restantes",
  "cases.deadline.replied": "Répondu",
  "cases.deadline.closed": "Clos",
  "cases.channel.phone": "Appel",
  "cases.channel.email": "Email",
  "cases.channel.chat": "Chat in-app",
  "cases.status.open": "Ouvert",
  "cases.status.waiting_on_customer": "En attente du client",
  "cases.status.closed": "Clos",
  "access.mine.title": "Ce que vous avez demandé",
  "access.mine.none": "Vous n'avez rien demandé.",
  "access.mine.what": "Quoi",
  "access.mine.state": "Où ça en est",
  "access.mine.asked": "Demandé",
  "access.mine.withWhom": "Chez",
  "access.mine.waiting": "En attente",
  "access.mine.granted": "Accordé",
  "access.mine.refused": "Refusé",
  "access.mine.overdue": "En retard",
  "access.mine.nobody": "Personne pour l'instant",
  "access.mine.answer": "Réponse",
  "access.mine.loading": "Chargement de vos demandes…",
  "access.mine.loadFailed": "Impossible de lire vos demandes.",
  "access.ask.title": "Demander plus",
  "access.ask.explain":
    "Construire la chose n'est pas une raison d'en détenir les données. Demandez le minimum qui vous permet de finir, pour la durée nécessaire. Cela part vers une personne habilitée, vous est accordé à vous seul, et prend fin tout seul.",
  "access.ask.what": "Ce dont vous avez besoin",
  "access.ask.until": "Jusqu'au",
  "access.ask.why": "Pourquoi vous en avez besoin",
  "access.ask.whyHint": "Ce que vous faites, et pourquoi c'est le minimum qui vous le permet",
  "access.ask.send": "Envoyer la demande",
  "access.ask.pickOne": "Choisissez ce qu'il vous faut",
  "access.ask.pastDate": "Indiquez une date future, ou laissez ouvert.",
  "access.ask.sent": "Envoyé. C'est le dossier {reference}.",
  "cases.kind.access": "Demande d'accès",
  "cases.kind.customer": "Client",
  "cases.kind": "Type",
  "cases.access.wants": "Demande",
  "cases.access.until": "Jusqu'au",
  "cases.access.grant": "Accorder",
  "cases.access.refuse": "Refuser",
  "cases.access.own": "Vous ne pouvez pas répondre à votre propre demande.",
  "cases.access.explain":
    "Accordé à cette personne seule, jamais à son équipe. Cela prend fin à la date demandée, et toute personne habilitée peut y mettre fin avant.",
  "cases.priority": "Priorité : {priority}",
  "cases.priority.low": "Basse",
  "cases.priority.normal": "Normale",
  "cases.priority.high": "Haute",
  "cases.priority.urgent": "Urgente",
  "cases.unknownCaller": "Appelant inconnu",
  "cases.unknownCallerPhone": "Appelant inconnu · {phone}",
  "cases.customerWithPhone": "{name} · {phone}",
  "cases.ownedBy": "attribué à {name}",
  "cases.unassigned": "non attribué",
  "cases.closedBecause": "Clos · motif : {reason}",
  "cases.timeline.aria": "Chronologie du dossier",
  "cases.timeline.empty": "Rien n'est encore noté sur ce dossier.",
  "cases.internal": "Interne",
  "cases.entry.call": "Appel",
  "cases.entry.callWithDuration": "Appel ({duration})",
  "cases.entry.note": "Note interne",
  "cases.entry.chatIn": "Message du client dans l'app",
  "cases.entry.chatOut": "Réponse dans l'app",
  "cases.entry.smsIn": "SMS du client",
  "cases.entry.smsOut": "Réponse par SMS",
  "cases.entry.smsOutTo": "SMS au {phone}",
  "cases.entry.emailIn": "Email du client",
  "cases.entry.emailOut": "Réponse par email",
  "cases.sms.pending": "En cours d'envoi",
  "cases.sms.sent": "Envoyé",
  "cases.sms.failed": "Échec de l'envoi",
  "cases.action.claim": "Prendre",
  "cases.action.reassign": "Réattribuer",
  "cases.action.link": "Lier à un client",
  "cases.action.relink": "Changer de client",
  "cases.action.close": "Clore",
  "cases.needs": "Nécessite {feature}",
  "cases.sending": "Envoi…",
  "cases.cancel": "Annuler",
  "cases.reload": "Recharger le dossier",
  "cases.reason": "Motif",
  "cases.reasonHint": "Enregistré dans le journal d'audit avec votre nom.",
  "cases.stale":
    "Ce dossier a changé pendant que vous le regardiez. Rechargez-le, vérifiez ce qui a changé, puis recommencez : votre texte est conservé.",
  "cases.claim.done": "{reference} est à vous.",
  "cases.claim.taken": "Trop tard : {name} vient de prendre ce dossier. Choisissez-en un autre.",
  "cases.composer.aria": "Écrire sur le dossier",
  "cases.composer.reply": "Répondre",
  "cases.composer.note": "Note interne",
  "cases.composer.call": "Noter un appel",
  "cases.reply.noChannel": "Aucun compte client lié : liez le client pour lui répondre dans l'app, ou rappelez-le.",
  "cases.reply.notified": "Le client reçoit une notification dans l'app. Jamais de SMS.",
  "cases.reply.label": "Réponse à {name}",
  "cases.reply.sendChat": "Envoyer dans l'app",
  "cases.reply.doneChat": "Réponse envoyée dans l'app.",
  "cases.reply.closed": "Ce dossier est clos : on n'y répond plus.",
  "cases.reply.unassigned": "Prenez ce dossier pour répondre au client.",
  "cases.reply.notOwner": "Seul le responsable du dossier répond au client. Demandez une réattribution si besoin.",
  "cases.refused.claimed": "Trop tard : quelqu'un d'autre vient de prendre ce dossier. Choisissez-en un autre.",
  "cases.refused.closed": "Ce dossier a été clos entre-temps. Rechargez-le.",
  "cases.refused.notOwner":
    "Ce dossier est à quelqu'un d'autre maintenant. Rechargez-le, ou demandez une réattribution.",
  "cases.refused.noCustomer": "Aucun compte client lié : liez le client pour lui répondre dans l'app, ou rappelez-le.",
  "cases.note.hint": "Visible par l'équipe seulement, jamais par le client.",
  "cases.note.label": "Note interne",
  "cases.note.submit": "Ajouter la note",
  "cases.note.done": "Note interne ajoutée.",
  "cases.call.label": "Ce qui a été dit (résumé interne)",
  "cases.call.minutes": "Durée (minutes)",
  "cases.call.submit": "Noter l'appel",
  "cases.call.done": "Appel noté.",
  "cases.reassign.title": "Réattribuer {reference}",
  "cases.reassign.from": "Actuellement attribué à {name}.",
  "cases.reassign.unassigned": "Ce dossier n'est encore attribué à personne.",
  "cases.reassign.to": "Nouveau responsable",
  "cases.reassign.choose": "Choisir un membre de l'équipe",
  "cases.reassign.loading": "Chargement de l'équipe…",
  "cases.reassign.toId": "Identifiant du membre de l'équipe",
  "cases.reassign.idHint": "Sans accès à la liste de l'équipe, collez l'identifiant du membre.",
  "cases.reassign.submit": "Réattribuer",
  "cases.reassign.done": "Dossier réattribué à {name}.",
  "cases.close.title": "Clore {reference}",
  "cases.close.body": "Le client ne pourra plus y répondre ; s'il revient, un nouveau dossier sera ouvert.",
  "cases.close.submit": "Clore le dossier",
  "cases.close.done": "{reference} est clos.",
  "cases.link.title": "Lier {reference} à un client",
  "cases.link.body": "Retrouvez le compte du client qui a contacté le support.",
  "cases.link.caller": "L'appelant a donné le {phone}. Retrouvez son compte.",
  "cases.link.current": "Actuellement lié à {name}.",
  "cases.link.submit": "Lier",
  "cases.link.done": "Dossier lié à {name}.",
  "cases.customer.search": "Chercher le client",
  "cases.customer.placeholder": "Nom, téléphone, email ou identifiant",
  "cases.customer.searchButton": "Chercher",
  "cases.customer.searching": "Recherche…",
  "cases.customer.none": "Aucun compte trouvé.",
  "cases.customer.results": "Comptes trouvés",
  "cases.customer.id": "Identifiant du compte client",
  "cases.customer.idHint": "La recherche nécessite {feature} : collez l'identifiant du compte.",
  "cases.newCall.title": "Noter un appel",
  "cases.newCall.who": "Qui a appelé ?",
  "cases.newCall.known": "Un client",
  "cases.newCall.unknown": "Un appelant inconnu",
  "cases.newCall.phone": "Numéro de l'appelant",
  "cases.newCall.phoneHint": "Au format international, par exemple +224 621 23 45 67.",
  "cases.newCall.subject": "Objet",
  "cases.newCall.internal": "Interne : le client ne voit pas ce résumé.",
  "cases.newCall.priority": "Priorité",
  "cases.newCall.submit": "Ouvrir le dossier",
  "cases.problem.reason": "Dites pourquoi, en quelques mots.",
  "cases.problem.body": "Écrivez quelque chose.",
  "cases.problem.tooLong": "Trop long : {max} caractères au plus.",
  "cases.problem.minutes": "La durée est un nombre entier de minutes, de 0 à {max}.",
  "cases.problem.phone": "Le numéro doit être au format international, par exemple +224621234567.",
  "cases.problem.pickCustomer": "Choisissez le client.",
  "cases.problem.customerId": "Cet identifiant de compte n'a pas le bon format.",
  "cases.problem.pickStaff": "Choisissez à qui confier le dossier.",
  "cases.problem.staffId": "Cet identifiant de membre n'a pas le bon format.",
  "cases.problem.subject": "Donnez au dossier un objet court.",
  // ── Vérifications ──
  "verifications.summary.open": "{count} en attente · la plus ancienne depuis {age}",
  "verifications.summary.none": "Aucune vérification en attente",
  "verifications.summary.shown": "{count} vérifications affichées",
  "verifications.filter.status": "Statut",
  "verifications.filter.status.open": "Toutes en attente",
  "verifications.filter.status.submitted": "À examiner",
  "verifications.filter.status.flagged": "Document banni",
  "verifications.filter.status.approved": "Approuvées",
  "verifications.filter.status.rejected": "Refusées",
  "verifications.filter.document": "Type de document",
  "verifications.filter.document.all": "Tous",
  "verifications.filter.document.national_id": "Carte d'identité",
  "verifications.filter.document.passport": "Passeport",
  "verifications.queue.aria": "File des vérifications, la plus ancienne en premier",
  "verifications.queue.loading": "Chargement de la file…",
  "verifications.queue.loadFailed": "La file n'a pas pu être chargée.",
  "verifications.queue.emptyOpen": "Aucune vérification n'attend : la file est vide.",
  "verifications.queue.empty": "Aucune vérification ne correspond à ces filtres.",
  "verifications.queue.waited": "Temps d'attente depuis l'envoi",
  "verifications.queue.end": "Fin de la file.",
  "verifications.flag.banned": "Document banni",
  "verifications.status.submitted": "À examiner",
  "verifications.status.flagged": "Document banni",
  "verifications.status.approved": "Approuvée",
  "verifications.status.rejected": "Refusée",
  "verifications.document.national_id": "Carte d'identité",
  "verifications.document.passport": "Passeport",
  "verifications.side.front": "Recto",
  "verifications.side.back": "Verso",
  "verifications.side.selfie": "Selfie",
  "verifications.detail.pick": "Choisissez une vérification dans la file pour l'examiner.",
  "verifications.detail.loading": "Chargement de la vérification…",
  "verifications.detail.loadFailed": "Cette vérification n'a pas pu être chargée.",
  "verifications.detail.notFound": "Aucune vérification ne porte cet identifiant. Rechargez la file.",
  "verifications.detail.noPhone": "sans téléphone",
  "verifications.detail.submitted": "envoyée le {date}",
  "verifications.detail.versionTitle": "Version examinée : une décision ne s'applique qu'à cette version",
  "verifications.banned.title": "Document banni",
  "verifications.banned.body":
    "Ce numéro correspond à un document banni le {date} · motif : {reason}. Il ne peut jamais être approuvé : refusez la vérification.",
  "verifications.banned.bodyNoReason":
    "Ce numéro correspond à un document banni. Il ne peut jamais être approuvé : refusez la vérification.",
  "verifications.banned.notTold": "Le pro n'est pas informé que son document est banni.",
  "verifications.images.aria": "Documents d'identité",
  "verifications.images.show": "Afficher les documents",
  "verifications.images.showAgain": "Afficher à nouveau",
  "verifications.images.loading": "Chargement…",
  "verifications.images.notice":
    "Chaque affichage est enregistré dans le journal d'audit, à votre nom, et figure dans l'historique du pro. Les liens expirent au bout de 2 minutes.",
  "verifications.images.recorded": "Affichage enregistré dans le journal d'audit · liens valables jusqu'à {time}",
  "verifications.images.hide": "Masquer",
  "verifications.images.expired":
    "Les liens ont expiré et les images ont été retirées de l'écran. Un nouvel affichage sera lui aussi enregistré.",
  "verifications.images.locked": "Les images d'identité nécessitent",
  "verifications.images.deleted": "Les images ont été supprimées le {date}, comme le prévoit la règle de conservation.",
  "verifications.col.field": "Champ",
  "verifications.col.read": "Lu sur le document",
  "verifications.col.typed": "Saisi par le pro",
  "verifications.col.match": "Concordance",
  "verifications.field.name": "Nom",
  "verifications.field.dateOfBirth": "Né le",
  "verifications.field.expiresOn": "Expire le",
  "verifications.field.documentNumber": "N° de document",
  "verifications.notAsked": "non demandé",
  "verifications.match.check": "À vérifier sur l'image",
  "verifications.match.same": "Concorde",
  "verifications.match.different": "Ne concorde pas",
  "verifications.match.readOnly": "Lu seulement",
  "verifications.match.expired": "Expiré",
  "verifications.match.valid": "En cours de validité",
  "verifications.match.banned": "Banni",
  "verifications.match.notBanned": "Non banni",
  "verifications.last4Hint":
    "Seuls les 4 derniers caractères du numéro sont conservés ({last4}) : comparez-les à l'image.",
  "verifications.standing.title": "Compte du pro aujourd'hui",
  "verifications.standing.verificationStatus": "Statut de vérification",
  "verifications.standing.approvalStatus": "Approbation du profil",
  "verifications.standing.isVerified": "Badge « vérifié »",
  "verifications.standing.status.unverified": "Non vérifié",
  "verifications.standing.status.pending": "En cours d'examen",
  "verifications.standing.status.verified": "Vérifié",
  "verifications.standing.status.rejected": "Refusé",
  "verifications.standing.approval.pending": "À approuver",
  "verifications.standing.approval.approved": "Approuvé",
  "verifications.standing.approval.rejected": "Non approuvé",
  "verifications.yes": "Oui",
  "verifications.no": "Non",
  "verifications.previous.title": "Envois précédents",
  "verifications.previous.none": "Premier envoi de ce pro.",
  "verifications.approve.button": "Approuver le pro",
  "verifications.reject.button": "Refuser",
  "verifications.decided": "Cette vérification est déjà tranchée.",
  "verifications.approve.banned":
    "Approbation impossible : le numéro correspond à un document banni. Refusez la vérification.",
  "verifications.approve.locked":
    "Approuver nécessite aussi APPROVE_PROS_STAFF_FEATURE_ENABLEMENT, que vos rôles n'incluent pas.",
  "verifications.decided.approved": "Approuvée par {name} le {date}.",
  "verifications.decided.rejected": "Refusée par {name} le {date}.",
  "verifications.decided.reasonCode": "Motif : {reason}",
  "verifications.decided.reason": "Note de l'équipe : « {reason} »",
  "verifications.outcome.approved": "{name} est approuvé : son profil est vérifié, on peut le trouver et l'engager.",
  "verifications.outcome.rejected": "Vérification refusée. Le pro est prévenu dans l'application, dans sa langue.",
  "verifications.approve.title": "Approuver {name}",
  "verifications.approve.body":
    "La pièce est acceptée et le pro devient vérifié : on peut le trouver et l'engager. La décision est enregistrée dans le journal d'audit avec votre motif.",
  "verifications.approve.reason": "Motif (enregistré dans le journal d'audit)",
  "verifications.approve.hint": "Par ex. « Carte lisible, selfie conforme ».",
  "verifications.approve.submit": "Approuver le pro",
  "verifications.approve.tooShort": "Donnez un motif d'au moins {min} caractères.",
  "verifications.reject.title": "Refuser la vérification de {name}",
  "verifications.reject.body": "Le pro pourra envoyer une nouvelle pièce depuis l'application.",
  "verifications.reject.preset": "Motif du refus",
  "verifications.reject.presetNone": "Choisir un motif…",
  "verifications.reject.code.photo_unreadable": "Photo illisible",
  "verifications.reject.code.selfie_mismatch": "Le selfie ne correspond pas",
  "verifications.reject.code.document_expired": "Document expiré",
  "verifications.reject.code.document_not_accepted": "Document non accepté",
  "verifications.reject.code.other": "Autre",
  "verifications.reject.reason": "Note pour l'équipe (facultative)",
  "verifications.reject.noteHint":
    "Jamais montrée au pro : il reçoit dans l'app le message du motif choisi, dans sa langue.",
  "verifications.reject.submit": "Refuser la vérification",
  "verifications.reject.pickCode": "Choisissez le motif du refus.",
  "verifications.reason.tooLong": "Le motif doit faire au plus {max} caractères.",
  "verifications.reason.count": "{count} caractères sur {max}",
  "verifications.dialog.version": "Décision sur la version {version}",
  "verifications.dialog.cancel": "Annuler",
  "verifications.dialog.sending": "Envoi…",
  "verifications.error.stale":
    "Cette vérification a changé pendant que vous la regardiez (quelqu'un d'autre l'a peut-être tranchée). Rechargez pour voir la version actuelle, puis décidez à nouveau.",
  "verifications.error.approveRefused":
    "Approbation refusée : vos rôles n'incluent pas REVIEW_VERIFICATIONS et APPROVE_PROS, ou le document vient d'être banni. Rechargez.",
  "verifications.error.rejectRefused":
    "Refus impossible : vos rôles n'incluent plus REVIEW_VERIFICATIONS. Demandez l'accès à un responsable.",
  "verifications.error.imagesRefused":
    "Images refusées : vos rôles n'incluent plus VIEW_ID_IMAGES. Cette tentative est enregistrée dans le journal d'audit.",
  "verifications.error.approveInvalid":
    "Approbation refusée : la vérification est déjà tranchée, le document est banni, ou le motif est invalide. Rechargez et vérifiez.",
  "verifications.error.rejectInvalid":
    "Refus rejeté : la vérification est déjà tranchée, ou la note dépasse {max} caractères.",
  "verifications.error.imagesDeleted":
    "Les images ne sont plus disponibles : elles ont été supprimées selon la règle de conservation, ou la vérification est introuvable.",
  "verifications.error.outcomeUnknown":
    "Pas de réponse du serveur : la décision a peut-être été enregistrée. Réessayez : le même clic ne peut pas être appliqué deux fois.",
} as const;

export type MessageId = keyof typeof fr;
